import React, { useState, useEffect } from 'react';
import {
  ThemeProvider, CssBaseline, TextField, FormControlLabel, Checkbox, Button,
  Grid, Box, Typography, Container, MenuItem, FormControl, InputLabel, Select, FormHelperText, Card, CardContent, Stepper, Step, LinearProgress, StepLabel,
  List, ListItem, ListItemText, ListItemIcon, Alert, CircularProgress, Divider, Paper, Avatar, IconButton, InputAdornment
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HearingDisabledIcon from '@mui/icons-material/HearingDisabled';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GenderIcon from '@mui/icons-material/Wc';
import logo from '../logo.png';
import { createTheme } from '@mui/material/styles';
import './styles.css';
import Footer from '../pages/Footer';
import Countdown from 'react-countdown';
import styled from 'styled-components';
import { 
  generateCodeVerifier, 
  generateCodeChallenge, 
  storePKCEState,
  getPKCEState,
  clearPKCEState,
  validatePKCE 
} from '../utils/pkce';
import { initializeFlow, detectActiveFlow, FLOW_TYPES } from '../utils/sessionManager';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import CommonAppBar from './CommonAppBar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import GradingIcon from '@mui/icons-material/Grading';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArticleIcon from '@mui/icons-material/Article';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CalculateIcon from '@mui/icons-material/Calculate';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NoteIcon from '@mui/icons-material/Note';
import DescriptionIcon from '@mui/icons-material/Description';
import FunctionsIcon from '@mui/icons-material/Functions';
import BiotechIcon from '@mui/icons-material/Biotech';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

// Custom Ethiopian Flag Component
const EthiopianFlag = ({ size = 28, color = 'rgb(18,82,166)' }) => (
  <Box
    sx={{
      width: size,
      height: size * 0.6,
      position: 'relative',
      borderRadius: '2px',
      overflow: 'hidden',
      border: '1px solid #e2e8f0'
    }}
  >
    {/* Green stripe (top) */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '33.33%',
        backgroundColor: '#009A49'
      }}
    />
    {/* Yellow stripe (middle) */}
    <Box
      sx={{
        position: 'absolute',
        top: '33.33%',
        left: 0,
        right: 0,
        height: '33.33%',
        backgroundColor: '#FEDD00'
      }}
    />
    {/* Red stripe (bottom) */}
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '33.33%',
        backgroundColor: '#EF3340'
      }}
    />
    {/* Blue circle with star (center) */}
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: size * 0.25,
        height: size * 0.25,
        borderRadius: '50%',
        backgroundColor: '#0052CC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Star */}
      <Box
        sx={{
          width: size * 0.12,
          height: size * 0.12,
          backgroundColor: '#FFD700',
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
        }}
      />
    </Box>
  </Box>
);

// Custom World Map Component
const WorldMap = ({ size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      position: 'relative',
      borderRadius: '50%',
      backgroundColor: '#1E90FF',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    }}
  >
    {/* North America */}
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '15%',
        width: '25%',
        height: '20%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
    {/* South America */}
    <Box
      sx={{
        position: 'absolute',
        top: '40%',
        left: '25%',
        width: '15%',
        height: '35%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
    {/* Europe */}
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        left: '45%',
        width: '20%',
        height: '15%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
    {/* Africa */}
    <Box
      sx={{
        position: 'absolute',
        top: '35%',
        left: '45%',
        width: '20%',
        height: '40%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
    {/* Asia */}
    <Box
      sx={{
        position: 'absolute',
        top: '25%',
        left: '65%',
        width: '25%',
        height: '25%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
    {/* Australia */}
    <Box
      sx={{
        position: 'absolute',
        bottom: '15%',
        right: '15%',
        width: '20%',
        height: '15%',
        backgroundColor: '#228B22',
        borderRadius: '2px'
      }}
    />
  </Box>
);

const theme = createTheme();

const isValidName = (name) => /^[a-zA-Z\s'/-]+$/.test(name);
const isValidEmail = (email) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
const isValidPhone = (phone) => /^[0-9]\d{0,9}$/.test(phone);

export default function RegistrationForm() {
  const [submitError, setSubmitError] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [faydaLoading, setFaydaLoading] = useState(false);
  const [savingFayda, setSavingFayda] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [fields, setFields] = useState({
    firstName: '',
    fatherName: '',
    grandFatherName: '',
    email: '',
    phoneNumber: '',
    file: null,
    bsccgpa: '',
    masterscgpa: '',
    gender: '',
    consent: false,
    institutionBsc: '',
    customInstitutionBsc: '',
    institutionMasters: '',
    customInstitutionMasters: '',
    examcenter: '',
    examCenter: '',
    selectedProgram: '',
    bscProgramName: '',
    phdProgramName: '',
    isBlind: 'No',
    isDeaf: 'No',
    nationalId: '',
    fanNumber: '',
    address: '',
    // New fields for non-Ethiopians
    nationality: '',
    countryOfOrigin: '',
    passportNumber: '',
    passportPhoto: null,
    photo: null,
  });
  const [errors, setErrors] = useState({});
  const [selectedProgram, setSelectedProgram] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [examCenters, setExamCenters] = useState([]);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const steps = ['Registration Type', 'Continue Registration with Fayda', 'Fayda Auth', 'Profile Review', 'Education', 'Submit'];
const nonEthiopianSteps = ['Personal Details', 'Contact & Passport', 'Accessibility Information', 'Preview & Confirm', 'Submit'];

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isNonEthiopian, setIsNonEthiopian] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  const address = userInfo?.address || {};
  const profilePicture = userInfo?.picture || '';



  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Check registration status
  useEffect(() => {
    // Initialize session protection for registration flow
    const initializeSession = () => {
      const currentFlow = detectActiveFlow();
      console.log('🔍 Current flow detected:', currentFlow);
      
      // If user is switching from another flow, clean up conflicting data
      if (currentFlow && currentFlow !== FLOW_TYPES.ETHIOPIAN_REGISTRATION && currentFlow !== FLOW_TYPES.NON_ETHIOPIAN_REGISTRATION) {
        console.log('🧹 Cleaning up conflicting data from other flows');
        initializeFlow(FLOW_TYPES.ETHIOPIAN_REGISTRATION);
      }
    };
    
    initializeSession();
    
    const checkRegistrationStatus = async () => {
      try {
        const response = await fetch('https://ngat.ethernet.edu.et/api/settings/registration-status', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
        });
        const data = await response.json();
        setRegistrationStatus(data);
      } catch (error) {
        console.error('Error checking registration status:', error);
        // Default to open if there's an error
        setRegistrationStatus({ registration_open: true, message: "Registration is currently open" });
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkRegistrationStatus();
  }, []);

  // Reset scroll position when active step changes - AGGRESSIVE RESET TO 0,0
  useEffect(() => {
    // Multiple aggressive methods to ensure scroll goes to absolute 0,0
    const resetToAbsoluteTop = () => {
      // Method 1: Reset main content container to absolute top
      const mainContainer = document.querySelector('main');
      if (mainContainer) {
        mainContainer.scrollTop = 0;
        mainContainer.scrollLeft = 0;
        // Force immediate reset
        mainContainer.scrollTo(0, 0);
        // Add aggressive reset class
        mainContainer.classList.add('force-scroll-reset');
        setTimeout(() => {
          mainContainer.classList.remove('force-scroll-reset');
        }, 200);
      }
      
      // Method 2: Reset ALL scrollable containers within main
      const allScrollableElements = document.querySelectorAll('main *');
      allScrollableElements.forEach(element => {
        if (element.scrollTop > 0 || element.scrollLeft > 0) {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        }
      });
      
      // Method 3: Force stepper to absolute top
      const stepperElement = document.querySelector('main .MuiStepper-root');
      if (stepperElement) {
        stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
        stepperElement.scrollTop = 0;
        stepperElement.scrollLeft = 0;
      }
      
      // Method 4: Reset any Material-UI containers
      const muiContainers = document.querySelectorAll('main .MuiCardContent-root, main .MuiPaper-root, main .MuiContainer-root, main .MuiBox-root');
      muiContainers.forEach(container => {
        container.scrollTop = 0;
        container.scrollLeft = 0;
        container.scrollTo(0, 0);
      });
      
      // Method 5: Multiple immediate resets with different timing
      setTimeout(() => {
        if (mainContainer) {
          mainContainer.scrollTop = 0;
          mainContainer.scrollLeft = 0;
          mainContainer.scrollTo(0, 0);
        }
        if (stepperElement) {
          stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
        }
      }, 10);
      
      setTimeout(() => {
        if (mainContainer) {
          mainContainer.scrollTop = 0;
          mainContainer.scrollLeft = 0;
        }
        if (stepperElement) {
          stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
        }
      }, 50);
      
      setTimeout(() => {
        if (mainContainer) {
          mainContainer.scrollTop = 0;
          mainContainer.scrollLeft = 0;
        }
        if (stepperElement) {
          stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
        }
      }, 100);
    };
    
    resetToAbsoluteTop();
  }, [activeStep]);

  // Handle Fayda callback
  useEffect(() => {
    // Check for callback parameters immediately
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    // If callback parameters exist, set loading immediately
    if (code || state) {
      console.log('🟡 Callback parameters detected - setting loading immediately');
      setLoading(true);
    }

    const handleFaydaCallback = async () => {
      try {
      console.log('🟡 Fayda callback handler started');

        // Check if we're on the callback URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
        const state = urlParams.get('state');
        
        // If no callback parameters, don't proceed
        if (!code && !state) {
          console.log('🟡 No callback parameters found - not a callback');
          setLoading(false);
          return;
        }
        
        // Get PKCE state from localStorage
        const { codeVerifier, state: storedState, timestamp } = getPKCEState();
      const savedFanNumber = localStorage.getItem('fanNumber');

        console.log('📥 Fayda Callback Received:', {
          hasCode: !!code,
          hasState: !!state,
          hasStoredState: !!storedState,
          hasCodeVerifier: !!codeVerifier,
          codeVerifierLength: codeVerifier?.length || 0,
          hasFanNumber: !!savedFanNumber,
          timestamp: timestamp ? new Date(parseInt(timestamp)).toISOString() : 'N/A'
        });

        if (!code) {
          console.warn('Missing authorization code — skipping callback');
          return;
        }

        if (!codeVerifier) {
          console.warn('Missing code verifier — skipping callback');
        return;
      }

      if (!savedFanNumber) {
        console.warn('FAN Number not found — going back to Step 1');
          setLoading(false);
        setActiveStep(0);
        return;
      }

        // Validate state parameter (CSRF protection)
        if (state !== storedState) {
          console.error('❌ State mismatch:', { received: state, stored: storedState });
          setError('Invalid state parameter. Please try authenticating again.');
          clearPKCEState();
          setLoading(false);
          setActiveStep(0);
          return;
        }

        console.log('🔄 Exchanging authorization code for tokens...');

        const faydaRes = await axios.get('https://ngat.ethernet.edu.et/api/auth/callback', {
          params: { 
            code, 
            code_verifier: codeVerifier,
            state: state
          },
          timeout: 25000,
        });
        
        console.log('✅ Token exchange successful');
        console.log('✅ Fayda profile fetched:', faydaRes.data);
        
        // Clear PKCE state after successful exchange
        clearPKCEState();
        
        setUserInfo(faydaRes.data.user || {});
        
        // Populate form fields with Fayda data
        const faydaUser = faydaRes.data.user || {};
        console.log('🔍 Fayda user data:', faydaUser);
        console.log('🔍 Saved FAN number:', savedFanNumber);
        
        if (faydaUser.name) {
          // Clean the name first - remove extra spaces and trim
          const cleanName = faydaUser.name.trim().replace(/\s+/g, ' ');
          const nameParts = cleanName.split(' ');
          
          // Handle name separation more intelligently
          let firstName = '';
          let fatherName = '';
          let grandFatherName = '';
          
          if (nameParts.length >= 3) {
            // If we have 3 or more parts, take the last 3 parts
            // This handles cases like "Mary Jane Doe Smith" -> "Mary Jane", "Doe", "Smith"
            const lastThreeParts = nameParts.slice(-3);
            if (nameParts.length > 3) {
              // If more than 3 parts, combine the first parts as firstName
              firstName = nameParts.slice(0, -2).join(' ');
              fatherName = lastThreeParts[1];
              grandFatherName = lastThreeParts[2];
            } else {
              // Exactly 3 parts
              firstName = lastThreeParts[0];
              fatherName = lastThreeParts[1];
              grandFatherName = lastThreeParts[2];
            }
          } else if (nameParts.length === 2) {
            // Only 2 parts - assign to firstName and fatherName
            firstName = nameParts[0];
            fatherName = nameParts[1];
            grandFatherName = '';
          } else if (nameParts.length === 1) {
            // Only 1 part - assign to firstName only
            firstName = nameParts[0];
            fatherName = '';
            grandFatherName = '';
          }
          
          const addressString = faydaUser.address ? 
            `${faydaUser.address.region || ''} ${faydaUser.address.zone || ''} ${faydaUser.address.woreda || ''} ${faydaUser.address.kebele || ''}`.trim() : '';
          
          setFields(prev => ({
            ...prev,
            firstName: firstName,
            fatherName: fatherName,
            grandFatherName: grandFatherName,
            gender: faydaUser.gender || '',
            phoneNumber: faydaUser.phone_number || '',
            address: addressString,
            nationality: 'Ethiopian',
            countryOfOrigin: 'Ethiopia',
            fanNumber: savedFanNumber || '', // Add FAN number from localStorage
            nationalId: savedFanNumber || '' // Use FAN number as national ID
          }));
          
          console.log('✅ Fields populated with Fayda data');
        } else {
          console.warn('⚠️ No name found in Fayda user data');
        }
        
        // Clear URL parameters to prevent redirect issues
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Navigate immediately to Profile Review
        console.log('🔄 Navigating to Profile Review (Step 3)');
        setActiveStep(3);
        // Reset scroll position to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Keep loading for a moment to ensure smooth transition
        setTimeout(() => {
          setLoading(false);
        }, 300);
        
      } catch (err) {
        console.error('❌ Fayda callback error:', err);
        
        // Clear PKCE state on error
        clearPKCEState();
        
        const errorMessage = err.response?.data?.message || err.message;
        setError('Verification failed: ' + errorMessage);
        setLoading(false);
        setActiveStep(0);
        
        // Log detailed error for debugging
        if (err.response) {
          console.error('Response data:', err.response.data);
          console.error('Response status:', err.response.status);
        }
      } finally {
        setLoading(false);
      }
    };

    const delay = setTimeout(() => {
      handleFaydaCallback();
    }, 300);

    return () => clearTimeout(delay);
  }, []);

  useEffect(() => {
    axios.get('https://ngat.ethernet.edu.et/api/signup-inist')
      .then(response => setInstitutions(response.data))
      .catch(error => console.error('Error fetching institutions:', error));

    axios.get('https://ngat.ethernet.edu.et/api/exam-centers')
      .then(response => setExamCenters(response.data))
      .catch(error => console.error('Error fetching exam centers:', error));
  }, []);

  const handleNext = () => {
    console.log('🔵 handleNext called, current step:', activeStep);
    console.log('🔵 isNonEthiopian:', isNonEthiopian);
    console.log('🔵 Current fields:', fields);
    
    if (!validateCurrentStep()) {
      console.log('❌ Validation failed, not proceeding to next step');
      setIsSuccess(false);
      return;
    }
    
    console.log('✅ Validation passed, proceeding to next step');
    
    // AGGRESSIVE scroll reset to absolute 0,0 position
    const mainContainer = document.querySelector('main');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
      mainContainer.scrollLeft = 0;
      mainContainer.scrollTo(0, 0);
    }
    
    // Reset ALL scrollable elements within main
    const allElements = document.querySelectorAll('main *');
    allElements.forEach(element => {
      if (element.scrollTop > 0 || element.scrollLeft > 0) {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      }
    });
    
    // Force stepper to absolute top
    const stepperElement = document.querySelector('main .MuiStepper-root');
    if (stepperElement) {
      stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
      stepperElement.scrollTop = 0;
      stepperElement.scrollLeft = 0;
    }
    
    setActiveStep((prevActiveStep) => {
      // Skip step 3 (Accessibility Information) if Aviation Academy is selected
      if (isNonEthiopian && prevActiveStep === 2 && fields.selectedProgram === 'Aviation Academy') {
        console.log('🔄 Skipping step 3 for Aviation Academy, going from', prevActiveStep, 'to', prevActiveStep + 2);
        return prevActiveStep + 2;
      }
      console.log('🔄 Setting activeStep from', prevActiveStep, 'to', prevActiveStep + 1);
      return prevActiveStep + 1;
    });
  };

  const handleBack = () => {
    // AGGRESSIVE scroll reset to absolute 0,0 position
    const mainContainer = document.querySelector('main');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
      mainContainer.scrollLeft = 0;
      mainContainer.scrollTo(0, 0);
    }
    
    // Reset ALL scrollable elements within main
    const allElements = document.querySelectorAll('main *');
    allElements.forEach(element => {
      if (element.scrollTop > 0 || element.scrollLeft > 0) {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      }
    });
    
    // Force stepper to absolute top
    const stepperElement = document.querySelector('main .MuiStepper-root');
    if (stepperElement) {
      stepperElement.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
      stepperElement.scrollTop = 0;
      stepperElement.scrollLeft = 0;
    }
    
    setActiveStep((prevActiveStep) => {
      // Skip step 3 (Accessibility Information) when going back if Aviation Academy is selected
      if (isNonEthiopian && prevActiveStep === 4 && fields.selectedProgram === 'Aviation Academy') {
        console.log('🔄 Skipping step 3 for Aviation Academy, going back from', prevActiveStep, 'to', prevActiveStep - 2);
        return prevActiveStep - 2;
      }
      return prevActiveStep - 1;
    });
  };

  const handleSignIn = () => {
    try {
      setFaydaLoading(true);
      
      // Generate proper PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = generateCodeChallenge(codeVerifier);
      const state = crypto.randomUUID(); // Use crypto.randomUUID() for better security
      
      // Validate PKCE parameters before proceeding
      if (!validatePKCE(codeVerifier, codeChallenge)) {
        console.error('❌ PKCE validation failed, aborting authentication');
        setFaydaLoading(false);
        return;
      }
      
      // Store PKCE state securely
      storePKCEState(codeVerifier, state);
      
      console.log('🚀 Starting Fayda SSO with PKCE:', {
        codeVerifierLength: codeVerifier.length,
        codeChallengeLength: codeChallenge.length,
        stateLength: state.length
      });
  
    const CLIENT_ID = 'fayda-partner-moe-api';
    const REDIRECT_URI = 'https://ngat.ethernet.edu.et/registration';
    const AUTHORIZATION_ENDPOINT = 'https://auth.fayda.et/authorize';
    const SCOPE = 'openid profile email';
      const ACR_VALUES = 'mosip:idp:acr:generated-code:biometrics';

    const claims = {
      userinfo: {
        name: { essential: true },
        phone_number: { essential: true },
        email: { essential: true },
        picture: { essential: true },
        gender: { essential: true },
        birthdate: { essential: true },
        address: { essential: true },
      },
    };
  
    const encodedClaims = encodeURIComponent(JSON.stringify(claims));
  
      const authUrl = `${AUTHORIZATION_ENDPOINT}?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&scope=${SCOPE}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256&acr_values=${ACR_VALUES}&claims=${encodedClaims}`;
  
      console.log('🔗 Redirecting to Fayda:', authUrl.substring(0, 100) + '...');
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('❌ Error in Fayda authentication:', error);
      setFaydaLoading(false);
      alert('Authentication error. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
  
    setFields((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFanNumberChange = (e) => {
    const { value } = e.target;
    // Only allow numbers and limit to 16 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 16);
    
    setFields((prev) => ({
      ...prev,
      fanNumber: numericValue,
    }));
    
    if (errors.fanNumber) {
      setErrors(prev => ({ ...prev, fanNumber: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, passportPhoto: 'File size must be less than 2MB' }));
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, passportPhoto: 'Please upload JPG, PNG, or PDF files only' }));
        return;
      }
      
      setFields(prev => ({
        ...prev,
        passportPhoto: file
      }));
      
      // Clear any existing errors
      if (errors.passportPhoto) {
        setErrors(prev => ({ ...prev, passportPhoto: '' }));
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB limit for photo)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, photo: 'Photo size must be less than 2MB' }));
        return;
      }
      
      // Check file type (only images)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, photo: 'Please upload JPG or PNG files only' }));
        return;
      }
      
      setFields(prev => ({
        ...prev,
        photo: file
      }));
      
      // Clear any existing errors
      if (errors.photo) {
        setErrors(prev => ({ ...prev, photo: '' }));
      }
    }
  };
  
  const validateCurrentStep = () => {
    console.log('🔍 validateCurrentStep called, activeStep:', activeStep);
    console.log('🔍 isNonEthiopian:', isNonEthiopian);
    console.log('🔍 Current fields:', fields);
    console.log('🔍 selectedProgram:', selectedProgram);
    
    const newErrors = {};
    
    if (isNonEthiopian) {
      // Non-Ethiopian validation
      if (activeStep === 1) {
        if (!fields.firstName) {
          newErrors.firstName = 'First name is required.';
        }
        if (!fields.fatherName) {
          newErrors.fatherName = 'Father\'s name is required.';
        }
        if (!fields.grandFatherName) {
          newErrors.grandFatherName = 'Grandfather\'s name is required.';
        }
        if (!fields.gender) {
          newErrors.gender = 'Gender is required.';
        }
        if (!fields.email) {
          newErrors.email = 'Email is required.';
        } else if (!isValidEmail(fields.email)) {
          newErrors.email = 'Please enter a valid email address.';
        }
        if (!fields.phoneNumber) {
          newErrors.phoneNumber = 'Phone number is required.';
        }
        if (!fields.selectedProgram) {
          newErrors.selectedProgram = 'Please select a program.';
        }
        if (!fields.examCenter) {
          newErrors.examCenter = 'Please select an exam center.';
        }
        if (!fields.photo) {
          newErrors.photo = 'Profile photo is required.';
        }
      } else if (activeStep === 2) {
        if (!fields.countryOfOrigin) {
          newErrors.countryOfOrigin = 'Country of origin is required.';
        }
        if (!fields.passportNumber) {
          newErrors.passportNumber = 'Passport number is required.';
        }
        if (!fields.passportPhoto) {
          newErrors.passportPhoto = 'Passport photo is required.';
        }
      } else if (activeStep === 3 && fields.selectedProgram !== 'Aviation Academy') {
        console.log('🔍 Validating step 3 (Accessibility Information)');
        console.log('🔍 fields.isBlind:', fields.isBlind);
        console.log('🔍 fields.isDeaf:', fields.isDeaf);
        

        
        if (!fields.isBlind || fields.isBlind === '') {
          console.log('❌ Visual impairment selection is missing');
          newErrors.isBlind = 'Visual impairment selection is required.';
        }
        if (!fields.isDeaf || fields.isDeaf === '') {
          console.log('❌ Hearing impairment selection is missing');
          newErrors.isDeaf = 'Hearing impairment selection is required.';
        }
        
        console.log('🔍 Validation errors for step 3:', newErrors);
        console.log('🔍 Total errors:', Object.keys(newErrors).length);
      }
        } else {
      // Ethiopian validation
      if (activeStep === 0) {
        if (!fields.fanNumber) {
          newErrors.fanNumber = 'FAN Number is required.';
        } else if (!/^\d{16}$/.test(fields.fanNumber)) {
          newErrors.fanNumber = 'FAN Number must be exactly 16 digits (numbers only).';
        }
      } else if (activeStep === 4) {
        console.log('🔍 Validating step 4 (Educational Information)');
        console.log('🔍 fields.email:', fields.email);
        console.log('🔍 fields.phoneNumber:', fields.phoneNumber);
        console.log('🔍 selectedProgram:', selectedProgram);
        console.log('🔍 fields.examcenter:', fields.examcenter);
        console.log('🔍 fields.file:', fields.file);
        
        if (!fields.email) {
          console.log('❌ Email is missing');
          newErrors.email = 'Email is required.';
        } else if (!isValidEmail(fields.email)) {
          console.log('❌ Email is invalid');
          newErrors.email = 'Please enter a valid email address.';
        }
        if (!fields.phoneNumber) {
          console.log('❌ Phone number is missing');
          newErrors.phoneNumber = 'Phone number is required.';
        }
        if (!selectedProgram) {
          console.log('❌ Program is not selected');
          newErrors.selectedProgram = 'Please select a program.';
        }
        if (!fields.examcenter) {
          console.log('❌ Exam center is not selected');
          newErrors.examcenter = 'Please select an exam center.';
        }
        if (!fields.isBlind || fields.isBlind === '') {
          console.log('❌ Visual impairment selection is missing');
          newErrors.isBlind = 'Please select visual impairment status.';
        }
        if (!fields.isDeaf || fields.isDeaf === '') {
          console.log('❌ Hearing impairment selection is missing');
          newErrors.isDeaf = 'Please select hearing impairment status.';
        }
        
        console.log('🔍 Total errors for step 4:', Object.keys(newErrors).length);
        // Commented out for development - profile picture is optional
        // if (!fields.file) {
        //   console.log('❌ Profile picture is missing');
        //   newErrors.file = 'Profile picture is required.';
        // }
        
        console.log('🔍 Validation errors for step 4:', newErrors);
      }
    }
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log('🔍 Final validation result:', isValid, 'Errors:', newErrors);
    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isNonEthiopian) {
        // For non-Ethiopians, send data to Laravel backend
    const formData = new FormData();
        
        // Add isEthiopian flag
        formData.append('isEthiopian', 0);
        
        // Add required fields exactly as backend expects
        formData.append('firstName', fields.firstName);
        formData.append('fatherName', fields.fatherName);
        formData.append('grandFatherName', fields.grandFatherName);
        formData.append('gender', fields.gender);
        formData.append('email', fields.email);
        formData.append('phoneNumber', fields.phoneNumber);
        formData.append('countryOfOrigin', fields.countryOfOrigin);
        formData.append('passportNumber', fields.passportNumber);
        formData.append('selectedProgram', fields.selectedProgram);
        formData.append('exam_center_id', fields.examCenter);
        
        // Fix accessibility fields format for backend
        formData.append('isBlind', fields.isBlind === true ? 'Yes' : 'No');
        formData.append('isDeaf', fields.isDeaf === true ? 'Yes' : 'No');
        
        // Fix consent format for backend
        formData.append('consent', fields.consent ? 'true' : 'false');
        
        // Add files
        if (fields.photo) {
          formData.append('profilePicture', fields.photo);
        }
        if (fields.passportPhoto) {
          formData.append('passportPhoto', fields.passportPhoto);
        }

      const response = await axios.post('https://ngat.ethernet.edu.et/api/register', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          },
      });

      setIsSuccess(true);
        // Show success message for non-Ethiopians
      } else {
        // For Ethiopians, proceed with normal registration
        const formData = new FormData();
        
        // Add isEthiopian flag for Ethiopian registrations
        formData.append('isEthiopian', 1);
        
        // Map Fayda data to form fields for Ethiopian registration
        // Priority: Fayda data > Form fields > Default values
        
        // Name fields - use Fayda data if available, otherwise form fields
        if (userInfo?.name) {
          // Clean the name first - remove extra spaces and trim
          const cleanName = userInfo.name.trim().replace(/\s+/g, ' ');
          const nameParts = cleanName.split(' ');
          
          // Handle name separation more intelligently
          let firstName = '';
          let fatherName = '';
          let grandFatherName = '';
          
          if (nameParts.length >= 3) {
            // If we have 3 or more parts, take the last 3 parts
            const lastThreeParts = nameParts.slice(-3);
            if (nameParts.length > 3) {
              // If more than 3 parts, combine the first parts as firstName
              firstName = nameParts.slice(0, -2).join(' ');
              fatherName = lastThreeParts[1];
              grandFatherName = lastThreeParts[2];
            } else {
              // Exactly 3 parts
              firstName = lastThreeParts[0];
              fatherName = lastThreeParts[1];
              grandFatherName = lastThreeParts[2];
            }
          } else if (nameParts.length === 2) {
            // Only 2 parts - assign to firstName and fatherName
            firstName = nameParts[0];
            fatherName = nameParts[1];
            grandFatherName = '';
          } else if (nameParts.length === 1) {
            // Only 1 part - assign to firstName only
            firstName = nameParts[0];
            fatherName = '';
            grandFatherName = '';
          }
          
          formData.append('firstName', firstName || fields.firstName || '');
          formData.append('fatherName', fatherName || fields.fatherName || '');
          formData.append('grandFatherName', grandFatherName || fields.grandFatherName || '');
        } else {
          formData.append('firstName', fields.firstName || '');
          formData.append('fatherName', fields.fatherName || '');
          formData.append('grandFatherName', fields.grandFatherName || '');
        }
        
        // FAN number - should be captured in step 1
        formData.append('fanNumber', fields.fanNumber || '');
        
        // Gender - use Fayda data if available, otherwise form fields
        formData.append('gender', userInfo?.gender || fields.gender || '');
        
        // Phone number - use Fayda data if available, otherwise form fields
        formData.append('phoneNumber', userInfo?.phone_number || fields.phoneNumber || '');
        
        // Email - from form fields
        formData.append('email', fields.email || '');
        
        // Set default values for Ethiopian registration
        formData.append('nationality', 'Ethiopian');
        formData.append('countryOfOrigin', 'Ethiopia');
        
        // Add Ethiopian-specific required fields
        // Use FAN number as national ID for Ethiopian registrations
        if (fields.fanNumber) {
          formData.append('nationalId', fields.fanNumber);
        }
        if (fields.address) {
          formData.append('address', fields.address);
        }
        
        // Add accessibility fields with proper format
        formData.append('isBlind', fields.isBlind === true ? 'Yes' : 'No');
        formData.append('isDeaf', fields.isDeaf === true ? 'Yes' : 'No');
        
        // Add specific required fields for Ethiopian registration
        console.log('🔍 Ethiopian registration fields check:');
        console.log('  - fields.selectedProgram:', fields.selectedProgram);
        console.log('  - selectedProgram state:', selectedProgram);
        console.log('  - examcenter:', fields.examcenter);
        console.log('  - photo:', fields.photo);
        console.log('  - fanNumber:', fields.fanNumber);
        console.log('  - nationalId:', fields.nationalId);
        
        // Use the state variable if the form field is empty
        const programToSend = fields.selectedProgram || selectedProgram;
        console.log('🔍 Final program to send:', programToSend);
        
        if (programToSend) {
          formData.append('selectedProgram', programToSend);
        }
        if (fields.examcenter) {
          formData.append('exam_center_id', fields.examcenter);
        }
        // Use uploaded photo or Fayda picture
        if (fields.photo) {
          formData.append('profilePicture', fields.photo);
          console.log('✅ Uploaded profile picture added to formData');
        } else if (userInfo?.picture) {
          // Use Fayda picture as profile picture
          try {
            const response = await fetch(userInfo.picture);
            const blob = await response.blob();
            const file = new File([blob], 'fayda-profile.jpg', { type: blob.type });
            formData.append('profilePicture', file);
            console.log('✅ Fayda profile picture added to formData');
          } catch (error) {
            console.error('❌ Failed to fetch Fayda picture:', error);
            // Create a placeholder file to satisfy backend validation
            const placeholderFile = new File([''], 'placeholder.jpg', { type: 'image/jpeg' });
            formData.append('profilePicture', placeholderFile);
          }
        } else {
          console.log('❌ No profile picture found (neither uploaded nor from Fayda)');
          // Create a placeholder file to satisfy backend validation
          const placeholderFile = new File([''], 'placeholder.jpg', { type: 'image/jpeg' });
          formData.append('profilePicture', placeholderFile);
        }
        
        // Add other form fields that are filled by user
        Object.keys(fields).forEach(key => {
          if (fields[key] !== null && fields[key] !== undefined && 
              !['firstName', 'fatherName', 'grandFatherName', 'gender', 'phoneNumber', 'email', 'fanNumber', 'nationalId', 'address', 'isBlind', 'isDeaf', 'selectedProgram', 'examcenter', 'photo'].includes(key)) {
            formData.append(key, fields[key]);
          }
        });
        
        // Fix consent format for backend
        formData.append('consent', fields.consent ? 'true' : 'false');
        

      const response = await axios.post('https://ngat.ethernet.edu.et/api/register', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          },
      });

        setIsSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 5000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultTheme = createTheme({
    palette: {
      primary: {
        main: 'rgb(18,82,166)',
      },
      background: {
        default: '#f5f5f5',
      },
    },
    typography: {
      fontFamily: '"Times New Roman", Times, serif',
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: '1.1rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.8rem',
      },
    },
    components: {
      MuiStepper: {
        styleOverrides: {
          root: {
            '& .MuiStepLabel-root .Mui-completed': {
              color: 'rgb(18,82,166)',
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: 'rgb(18,82,166)',
          },
        },
      },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={defaultTheme}>
      <div className="page-container" style={{
        minHeight: '100vh',
        width: '100vw',
        maxWidth: '100vw',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        margin: 0,
        padding: 0,
        position: 'relative'
      }}>
        <CssBaseline />
        <style>
          {`
            /* Global AppBar full width */
            .MuiAppBar-root {
              width: 100vw !important;
              max-width: 100vw !important;
              left: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              position: relative !important;
            }
            
            .MuiToolbar-root {
              width: 100% !important;
              margin: 0 !important;
              padding-left: 16px !important;
              padding-right: 16px !important;
              box-sizing: border-box !important;
            }
            
            /* Remove any container constraints */
            .MuiContainer-root {
              max-width: none !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            /* Ensure body has no margins */
            body {
              margin: 0 !important;
              padding: 0 !important;
              overflow-x: hidden !important;
            }
            
            /* Ensure page container doesn't limit AppBar */
            .page-container {
              width: 100vw !important;
              max-width: 100vw !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow-x: hidden !important;
            }
            
            /* Override any Material-UI theme constraints */
            #root {
              width: 100vw !important;
              max-width: 100vw !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow-x: hidden !important;
            }
            
            /* Override any potential CSS reset issues */
            * {
              box-sizing: border-box !important;
            }
            
            /* Force AppBar to break out of any container */
            .MuiAppBar-root {
              position: relative !important;
              left: 50% !important;
              right: 50% !important;
              margin-left: -50vw !important;
              margin-right: -50vw !important;
              width: 100vw !important;
              max-width: 100vw !important;
            }
            
            /* Additional mobile-specific overrides */
            @media (max-width: 600px) {
              .MuiAppBar-root {
                position: relative !important;
                left: 0 !important;
                right: 0 !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
                width: 100vw !important;
                max-width: 100vw !important;
                transform: none !important;
              }
            }
            
            /* CommonAppBar specific overrides */
            
            /* TextField Fixes for Registration */
            .MuiTextField-root .MuiOutlinedInput-root {
              min-height: 48px !important;
            }
            .MuiTextField-root .MuiOutlinedInput-input {
              padding: 14px 14px !important;
            }
            .MuiTextField-root .MuiInputLabel-root {
              line-height: 1.2 !important;
            }
            .MuiTextField-root .MuiOutlinedInput-notchedOutline {
              border-radius: 8px !important;
            }
            .MuiFormControl-root .MuiInputLabel-root {
              line-height: 1.2 !important;
            }
            .MuiTextField-root .MuiInputLabel-shrink {
              transform: translate(14px, -9px) scale(0.75) !important;
            }
            
            /* Mobile specific overrides */
            @media (max-width: 600px) {
              .MuiTextField-root .MuiOutlinedInput-root {
                min-height: 48px !important;
              }
              .MuiTextField-root .MuiOutlinedInput-input {
                padding: 12px 14px !important;
              }
            }
            
            /* Remove scrollable content for better UX */
            .registration-form-scrollable {
              overflow: visible !important;
              max-height: none !important;
            }
            
            /* Ensure full content visibility on mobile */
            @media (max-width: 600px) {
              .MuiPaper-root {
                max-height: none !important;
                overflow: visible !important;
              }
              .MuiCard-root {
                max-height: none !important;
                overflow: visible !important;
              }
              .MuiCardContent-root {
                max-height: none !important;
                overflow: visible !important;
              }
            }
            
            /* Main Card Mobile Scrolling */
            @media (max-width: 600px) {
              .MuiCard-root {
                overflow: visible !important;
                max-height: none !important;
              }
              
              .MuiCardContent-root {
                overflow: visible !important;
                max-height: none !important;
              }
              
              .MuiCardContent-root::-webkit-scrollbar {
                display: none !important;
              }
              
              .MuiCardContent-root::-ms-scrollbar {
                display: none !important;
              }
            }
            
            /* Page Container Mobile Scrolling - NO LONGER TARGETING WHOLE PAGE */
            @media (max-width: 600px) {
              .page-container {
                overflow-y: auto !important;
                overflow-x: hidden !important;
                -webkit-overflow-scrolling: touch !important;
                height: 100vh !important;
                position: relative !important;
              }
            }
            
            /* AGGRESSIVE scroll reset utility - FORCE 0,0 POSITION */
            @media (max-width: 600px) {
              .scroll-reset {
                scroll-behavior: auto !important;
                scroll-top: 0 !important;
                scroll-left: 0 !important;
              }
              
              /* Force ALL stepper content elements to reset */
              main,
              main *,
              main .MuiCardContent-root,
              main .MuiPaper-root,
              main .MuiContainer-root,
              main .MuiBox-root,
              main .MuiStepper-root {
                scroll-behavior: auto !important;
                scroll-top: 0 !important;
                scroll-left: 0 !important;
              }
              
              /* Force immediate scroll reset */
              main {
                scroll-behavior: auto !important;
                scroll-top: 0 !important;
                scroll-left: 0 !important;
                overflow-anchor: none !important;
              }
            }
            
            /* Additional aggressive reset for all devices */
            main {
              scroll-behavior: auto !important;
            }
            
            main * {
              scroll-behavior: auto !important;
            }
            
            /* Force scroll reset class - ULTIMATE RESET */
            .force-scroll-reset {
              scroll-top: 0 !important;
              scroll-left: 0 !important;
              scroll-behavior: auto !important;
              overflow-anchor: none !important;
            }
            
            .force-scroll-reset * {
              scroll-top: 0 !important;
              scroll-left: 0 !important;
              scroll-behavior: auto !important;
            }
            .CommonAppBar-root {
              width: 100vw !important;
              max-width: 100vw !important;
              left: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            @media (max-width: 600px) {
              /* Mobile AppBar full width - no gaps */
              .MuiAppBar-root {
                width: 100vw !important;
                max-width: 100vw !important;
                left: 0 !important;
                right: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                position: relative !important;
              }
              
              .MuiToolbar-root {
                width: 100% !important;
                margin: 0 !important;
                padding-left: 8px !important;
                padding-right: 8px !important;
                box-sizing: border-box !important;
              }
              
              /* Mobile page container */
              .page-container {
                width: 100vw !important;
                max-width: 100vw !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow-x: hidden !important;
              }
              
              /* Mobile body */
              body {
                margin: 0 !important;
                padding: 0 !important;
                overflow-x: hidden !important;
                width: 100vw !important;
              }
              
              /* Mobile html */
              html {
                margin: 0 !important;
                padding: 0 !important;
                width: 100vw !important;
                overflow-x: hidden !important;
              }
              
              .MuiPopover-root {
                z-index: 99999 !important;
              }
              .MuiMenu-root {
                z-index: 99999 !important;
              }
              .MuiPaper-root {
                max-width: calc(100vw - 32px) !important;
              }
              .MuiSelect-select {
                font-size: 16px !important;
              }
              .MuiStepper-root {
                overflow-x: auto !important;
                padding: 0 8px !important;
              }
              .MuiStep-root {
                min-width: auto !important;
                flex-shrink: 0 !important;
              }
              .MuiStepLabel-label {
                font-size: 0.75rem !important;
                line-height: 1.2 !important;
                word-break: break-word !important;
              }
            }
          `}
        </style>
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
            position: 'relative',
            overflow: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(18,82,166,0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(18,82,166,0.08) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(18,82,166,0.06) 0%, transparent 50%)
              `,
              pointerEvents: 'none'
            }
          }}
        >
          {/* Connected Particle Network Effect - Positioned behind main content */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(2px 2px at 20px 30px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 40px 70px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 90px 40px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 130px 80px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 160px 30px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 200px 90px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 240px 50px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 280px 120px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 320px 40px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 360px 100px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 400px 60px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 440px 140px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 480px 30px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 520px 80px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 560px 50px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 600px 110px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 640px 70px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 680px 130px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 720px 40px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 760px 90px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 800px 60px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 840px 120px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 880px 80px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 920px 140px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 960px 50px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 1000px 100px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 1040px 70px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 1080px 130px, rgba(18,82,166,0.08), transparent),
                radial-gradient(2px 2px at 1120px 40px, rgba(18,82,166,0.15), transparent),
                radial-gradient(2px 2px at 1160px 90px, rgba(18,82,166,0.12), transparent),
                radial-gradient(1px 1px at 1200px 60px, rgba(18,82,166,0.1), transparent),
                radial-gradient(1px 1px at 1240px 110px, rgba(18,82,166,0.08), transparent)
              `,
              backgroundSize: { xs: '320px 40px', md: '1280px 160px' },
              backgroundRepeat: 'repeat',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
          {/* Slightly More Visible Molecular Structure */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(4px 4px at 80px 80px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 240px 240px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 400px 400px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 560px 560px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 720px 720px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 880px 880px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1040px 1040px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1200px 1200px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1360px 1360px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1520px 1520px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1680px 1680px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 1840px 1840px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2000px 2000px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2160px 2160px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2320px 2320px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2480px 2480px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2640px 2640px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2800px 2800px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 2960px 2960px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 3120px 3120px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 3280px 3280px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 3440px 3440px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 3600px 3600px, rgba(18,82,166,0.2), transparent),
                radial-gradient(4px 4px at 3760px 3760px, rgba(18,82,166,0.2), transparent)
              `,
              backgroundSize: { xs: '960px 960px', md: '3840px 3840px' },
              backgroundRepeat: 'repeat',
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
          {/* Subtle Molecular Bond Lines */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(45deg, transparent 49.9%, rgba(18,82,166,0.04) 49.9%, rgba(18,82,166,0.04) 50.1%, transparent 50.1%),
                linear-gradient(-45deg, transparent 49.9%, rgba(18,82,166,0.04) 49.9%, rgba(18,82,166,0.04) 50.1%, transparent 50.1%),
                linear-gradient(90deg, transparent 49.9%, rgba(18,82,166,0.03) 49.9%, rgba(18,82,166,0.03) 50.1%, transparent 50.1%),
                linear-gradient(0deg, transparent 49.9%, rgba(18,82,166,0.03) 49.9%, rgba(18,82,166,0.03) 50.1%, transparent 50.1%)
              `,
              backgroundSize: { xs: '50px 50px, 50px 50px, 25px 25px, 25px 25px', md: '200px 200px, 200px 200px, 100px 100px, 100px 100px' },
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
          {/* Floating Connected Structures - Positioned in corners */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              right: '5%',
              width: { xs: 125, md: 250 },
              height: { xs: 90, md: 180 },
              opacity: 0.1,
              backgroundImage: `
                radial-gradient(circle at 30% 30%, rgba(18,82,166,0.2) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(18,82,166,0.15) 0%, transparent 50%),
                radial-gradient(circle at 50% 50%, rgba(18,82,166,0.12) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '15%',
              left: '3%',
              width: { xs: 110, md: 220 },
              height: { xs: 75, md: 150 },
              opacity: 0.08,
              backgroundImage: `
                radial-gradient(circle at 50% 50%, rgba(18,82,166,0.18) 0%, transparent 50%),
                radial-gradient(circle at 20% 80%, rgba(18,82,166,0.12) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(18,82,166,0.12) 0%, transparent 50%)
              `,
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
          {/* Moderately Visible Grid Structure */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(rgba(18,82,166,0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(18,82,166,0.04) 1px, transparent 1px)
              `,
              backgroundSize: { xs: '15px 15px', md: '60px 60px' },
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
          {/* Secondary Grid */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(rgba(18,82,166,0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(18,82,166,0.03) 1px, transparent 1px)
              `,
              backgroundSize: { xs: '30px 30px', md: '120px 120px' },
              pointerEvents: 'none',
              zIndex: 0
            }}
          />
          
        <CommonAppBar />

          <Container component="main" sx={{ 
            maxWidth: 'md', 
            py: { xs: 2, md: 3 },
            px: { xs: 1, md: 2 },
            overflow: 'auto',
            minHeight: '100vh'
          }}>
            {/* Engaging Header Card - White Background */}
            <Box sx={{ 
              mb: { xs: 3, md: 4 },
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}>
              <Card sx={{ 
                width: '100%',
                maxWidth: '900px',
                backgroundColor: '#ffffff',
                border: '2px solid rgba(18,82,166,0.2)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                overflow: 'visible',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 50%, rgb(18,82,166) 100%)',
                  borderRadius: '3px 3px 0 0'
                }
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  {/* Title */}
                  <Typography component="h1" variant="h4" sx={{ 
                    mb: 2, 
                    color: 'rgb(18,82,166)',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                    lineHeight: 1.3
                  }}>
                    National Graduate Admission Test (NGAT) Registration
                  </Typography>
                  
                  {/* Video Link and Deadline Row */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 2, sm: 3 },
                    flexWrap: 'wrap'
                  }}>
                    {/* Registration Guide Video */}
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      backgroundColor: 'rgba(18,82,166,0.08)',
                      borderRadius: 2,
                      border: '1px solid rgba(18,82,166,0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(18,82,166,0.12)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(18,82,166,0.15)'
                      }
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: '#64748b', 
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                      }}>
                        📺 Registration guide video:
                      </Typography>
                      <a 
                        href="https://youtu.be/4ygxMMcrEcg" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: 'rgb(18,82,166)', 
                          textDecoration: 'none',
                          fontWeight: '700',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s ease-in-out'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = 'rgb(14,65,130)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = 'rgb(18,82,166)';
                        }}
                      >
                        Watch Now →
                      </a>
                    </Box>
                    
                    {/* Deadline */}
                    {registrationStatus?.deadline && (
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        backgroundColor: 'rgba(18,82,166,0.08)',
                        borderRadius: 2,
                        border: '1px solid rgba(18,82,166,0.2)'
                      }}>
                        <CalendarTodayIcon sx={{ color: 'rgb(18,82,166)', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ 
                          color: '#64748b', 
                          fontWeight: 500,
                          fontSize: { xs: '0.8rem', md: '0.875rem' }
                        }}>
                          Deadline:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'rgb(18,82,166)', 
                          fontWeight: 600,
                          fontFamily: '"Times New Roman", Times, serif',
                          fontSize: { xs: '0.8rem', md: '0.875rem' }
                        }}>
                          {registrationStatus.deadline}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Registration Status Check */}
            {isCheckingStatus ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
              </Box>
            ) : !registrationStatus?.registration_open ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Card sx={{ 
                  width: '100%', 
                  maxWidth: '900px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" sx={{ color: '#d32f2f', mb: 2, fontWeight: 600 }}>
                        Registration Currently Closed
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
                        The National Graduate Admission Test (NGAT) registration is currently not available. 
                        Please check back later for updates on registration availability.
                      </Typography>
                      
                      {/* Deadline Display - Compact inline version */}
                      {registrationStatus?.deadline && (
                        <Box sx={{ 
                          mb: 3,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          backgroundColor: 'rgba(18,82,166,0.06)', 
                          borderRadius: 1,
                          border: '1px solid rgba(18,82,166,0.12)'
                        }}>
                          <CalendarTodayIcon sx={{ color: 'rgb(18,82,166)', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                            Deadline:
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 600,
                            fontFamily: '"Times New Roman", Times, serif'
                          }}>
                            {registrationStatus.deadline}
                          </Typography>
                        </Box>
                      )}
                      
                      <Typography variant="body2" sx={{ color: '#888', fontFamily: '"Times New Roman", Times, serif', mt: 2 }}>
                        For inquiries, please contact the Ministry of Education through official channels.
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      onClick={() => navigate('/')}
                      sx={{ 
                        backgroundColor: 'rgb(18,82,166)',
                        '&:hover': { backgroundColor: 'rgb(14,66,133)' }
                      }}
                    >
                      Return to Home
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <Stepper 
                  activeStep={activeStep} 
                  sx={{ 
                    width: '100%', 
                    maxWidth: '900px', 
                    mb: { xs: 2, md: 3 },
                    display: loading ? 'none' : 'flex',
                    overflow: 'auto',
                    px: { xs: 1, md: 0 },
                    '& .MuiStepConnector-line': {
                      borderTopWidth: 1,
                      borderColor: 'rgba(18,82,166,0.3)',
                    },
                    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                      borderColor: 'rgb(18,82,166)',
                    },
                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                      borderColor: 'rgb(18,82,166)',
                    },
                    '& .MuiStepLabel-root': {
                      padding: { xs: '0 4px', md: '0 8px' },
                    },
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      lineHeight: 1.2,
                    },
                    '& .MuiStep-root': {
                      minWidth: { xs: 'auto', md: 'auto' },
                    }
                  }}
                >
                  {(isNonEthiopian ? nonEthiopianSteps : steps).map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

              {/* Global Loading Overlay */}
              {loading && (
                <Box sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 99999,
                  backdropFilter: 'blur(8px)'
                }}>
                  <CircularProgress size={80} sx={{ color: 'rgb(18,82,166)', mb: 4 }} />
                  <Typography variant="h5" sx={{ color: 'rgb(18,82,166)', fontWeight: 700, mb: 2 }}>
                    🔄 Processing Authentication
                </Typography>
                  <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
                    Please wait while we verify your identity and retrieve your profile information...
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', mt: 2 }}>
                    This may take a few moments
                  </Typography>
                </Box>
              )}

              <Card sx={{ 
                width: '100%', 
                maxWidth: '900px',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                position: 'relative',
                zIndex: 1,
                display: loading ? 'none' : 'block',
                mx: { xs: 1, md: 'auto' },
              }}>
                <CardContent sx={{ 
                  p: { xs: 2, md: 3 }
                }}>
                  <Box 
                    component="form" 
                    noValidate 
                    onSubmit={handleSubmit}
                    sx={{
                      height: '100%'
                    }}
                  >
                  {activeStep === 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 700,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <BadgeIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Select Your Registration Type
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Choose your nationality to determine the appropriate registration process for your application.
                          </Typography>
                        </Box>

                        {/* Selection Cards */}
                        <Grid container spacing={3} sx={{ width: '100%', maxWidth: 600 }}>
                          {/* Ethiopian Option */}
                          <Grid item xs={12} md={6}>
                            <Paper 
                              elevation={0}
                              onClick={() => {
                                setFields(prev => ({ ...prev, nationality: 'Ethiopian' }));
                                setIsNonEthiopian(false);
                                setActiveStep(1);
                              }}
                              sx={{ 
                                p: { xs: 2, md: 4 },
                                textAlign: 'center',
                                cursor: 'pointer',
                                border: '2px solid #e2e8f0',
                                borderRadius: 3,
                                backgroundColor: '#ffffff',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                  borderColor: 'rgb(18,82,166)',
                                  boxShadow: '0 8px 25px rgba(18,82,166,0.15)',
                                  transform: 'translateY(-2px)',
                                },
                                position: 'relative',
                              }}
                            >
                              {/* Decorative accent */}
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                              }} />
                              
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(18,82,166,0.1)',
                                  mb: 2
                                }}>
                                  <EthiopianFlag size={28} />
                                </Box>
                                
                                <Typography variant="h6" sx={{ 
                                  mb: 2, 
                                  color: '#1e293b',
                                  fontWeight: 600
                                }}>
                                  Ethiopian Applicants
                                </Typography>
                                
                                <Typography variant="body2" sx={{ 
                                  color: '#64748b',
                                  lineHeight: 1.6,
                                  mb: 3
                                }}>
                                  Register using your national identification through Fayda authentication for a streamlined process.
                                </Typography>
                                
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1,
                                  color: 'rgb(18,82,166)',
                                  fontWeight: 600,
                                  fontSize: '0.875rem'
                                }}>
                                  <span>Continue Registration with Fayda</span>
                                  <Box sx={{ fontSize: 16 }}>→</Box>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>

                          {/* Non-Ethiopian Option */}
                          <Grid item xs={12} md={6}>
                            <Paper 
                              elevation={0}
                              onClick={() => {
                                if (!registrationStatus?.enable_forign_register) return;
                                setFields(prev => ({ ...prev, nationality: 'Non-Ethiopian' }));
                                setIsNonEthiopian(true);
                                setActiveStep(1);
                              }}
                              sx={{ 
                                p: { xs: 2, md: 4 },
                                textAlign: 'center',
                                cursor: registrationStatus?.enable_forign_register ? 'pointer' : 'not-allowed',
                                border: '2px solid #e2e8f0',
                                borderRadius: 3,
                                backgroundColor: registrationStatus?.enable_forign_register ? '#ffffff' : '#f5f5f5',
                                opacity: registrationStatus?.enable_forign_register ? 1 : 0.6,
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': registrationStatus?.enable_forign_register ? {
                                  borderColor: 'rgb(18,82,166)',
                                  boxShadow: '0 8px 25px rgba(18,82,166,0.15)',
                                  transform: 'translateY(-2px)',
                                } : {},
                                position: 'relative',
                              }}
                            >
                              {/* Decorative accent */}
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                              }} />
                              
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(18,82,166,0.1)',
                                  mb: 2
                                }}>
                                  <WorldMap size={28} />
                                </Box>
                                
                                <Typography variant="h6" sx={{ 
                                  mb: 2, 
                                  color: '#1e293b',
                                  fontWeight: 600
                                }}>
                                  Non-Ethiopian Applicants
                                </Typography>
                                
                                {!registrationStatus?.enable_forign_register && (
                                  <Box sx={{ 
                                    mb: 2, 
                                    p: 1.5, 
                                    backgroundColor: '#fff3e0', 
                                    borderRadius: 1.5,
                                    border: '1px solid #ffb74d'
                                  }}>
                                    <Typography variant="body2" sx={{ 
                                      color: '#e65100',
                                      fontWeight: 500,
                                      fontSize: '0.8rem'
                                    }}>
                                      ⚠️ Currently Unavailable
                                    </Typography>
                                  </Box>
                                )}
        
                                <Typography variant="body2" sx={{ 
                                  color: '#64748b',
                                  lineHeight: 1.6,
                                  mb: 3
                                }}>
                                  Complete your registration by providing personal information and passport documentation.
                                </Typography>
                                
                                {registrationStatus?.enable_forign_register && (
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    color: 'rgb(18,82,166)',
                                    fontWeight: 600,
                                    fontSize: '0.875rem'
                                  }}>
                                    <span>Start Registration</span>
                                    <Box sx={{ fontSize: 16 }}>→</Box>
                                  </Box>
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Help Section */}
                        <Box sx={{ 
                          mt: 4,
                          p: 3,
                          backgroundColor: 'rgba(18,82,166,0.03)',
                          borderRadius: 2,
                          border: '1px solid rgba(18,82,166,0.1)',
                          maxWidth: 480,
                          width: '100%'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(18,82,166,0.1)',
                              mr: 1
                            }}>
                              <Typography sx={{ fontSize: 12, color: 'rgb(18,82,166)', fontWeight: 600 }}>?</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: 'rgb(18,82,166)', 
                              fontSize: '0.875rem',
                              fontWeight: 600
                            }}>
                              Need Help?
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ 
                            color: '#64748b', 
                            fontSize: '0.8rem', 
                            textAlign: 'center',
                            lineHeight: 1.5
                          }}>
                            Watch our <a href="https://youtu.be/4ygxMMcrEcg" style={{ color: 'rgb(18,82,166)', textDecoration: 'underline', fontWeight: 500 }}>registration guide</a> or contact support <a href="https://t.me/ngatsupportofficial" target="_blank" rel="noopener noreferrer" style={{ color: 'rgb(18,82,166)', textDecoration: 'underline', fontWeight: 500 }}>via Telegram</a> for assistance.
                          </Typography>
                        </Box>
                        
                        {/* Bottom padding to ensure help section is fully visible */}
                        <Box sx={{ height: { xs: 20, md: 40 } }} />
                      </Box>
                    )}

                    {activeStep === 1 && !isNonEthiopian && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 600,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <BadgeIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Verify Your Identity
                </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Enter your FAN (Fayda Alias Number) to securely verify your identity and begin the registration process.
                          </Typography>
                        </Box>

                        {/* Main Form Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 480,
                          p: { xs: 2, md: 4 },
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                          }} />
                          
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="h6" sx={{ 
                              mb: 3, 
                              color: '#1e293b',
                              fontWeight: 600,
                              textAlign: 'center'
                            }}>
                              FAN Number Verification
                </Typography>
                            
                        <TextField
                          required
                          fullWidth
                          id="fanNumber"
                          name="fanNumber"
                          label="FAN Number"
                              placeholder="Enter your 16-digit FAN number"
                          value={fields.fanNumber || ''}
                          onChange={handleFanNumberChange}
                          error={!!errors.fanNumber}
                              helperText={errors.fanNumber || "Enter exactly 16 digits (numbers only)"}
                              sx={{ 
                                mb: 4,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: '#f8fafc',
                                  minHeight: '48px',
                                  '&:hover': {
                                    backgroundColor: '#f1f5f9',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgb(18,82,166)',
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: '#ffffff',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(18,82,166)',
                                    borderWidth: 2,
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#64748b',
                                  '&.Mui-focused': {
                                    color: 'rgb(18,82,166)',
                                  },
                                },
                              }}
                              InputProps={{
                                style: { 
                                  fontSize: '1.1rem',
                                  fontWeight: 500,
                                  letterSpacing: '0.5px',
                                  padding: '14px 14px'
                                },
                                endAdornment: (
                                  <Typography variant="caption" sx={{ color: fields.fanNumber.length === 16 ? 'success.main' : '#64748b', mr: 1 }}>
                                    {fields.fanNumber.length}/16
                                  </Typography>
                                )
                              }}
                            />
                            
                            
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 2.5,
                              alignItems: 'stretch'
                            }}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!fields.fanNumber || fields.fanNumber.length !== 16}
                          onClick={() => {
                            localStorage.setItem('fanNumber', fields.fanNumber);
                                  // Simulate Fayda authentication success and skip to profile review
                                  const mockUserInfo = {
                                    name: "John Doe",
                                    birthdate: "1990-01-15",
                                    gender: "Male",
                                    phone_number: "+251912345678",
                                    address: {
                                      region: "Addis Ababa",
                                      zone: "Central",
                                      woreda: "Kolfe Keranio",
                                      kebele: "01"
                                    },
                                    picture: "https://via.placeholder.com/150x150/1976d2/ffffff?text=JD"
                                  };
                                  setUserInfo(mockUserInfo);
                                  setActiveStep(2); // Skip to profile review step
                                }}
                                sx={{ 
                                  py: 2,
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  backgroundColor: 'rgb(18,82,166)',
                                  boxShadow: '0 4px 14px 0 rgba(18,82,166,0.39)',
                                  '&:hover': {
                                    backgroundColor: 'rgb(14,65,130)',
                                    boxShadow: '0 6px 20px 0 rgba(18,82,166,0.5)',
                                    transform: 'translateY(-1px)',
                                  },
                                  '&:disabled': {
                                    background: '#e2e8f0',
                                    color: '#94a3b8',
                                    boxShadow: 'none',
                                    transform: 'none',
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                              >
                                Continue Registration with Fayda
                        </Button>
                                
                              <Divider sx={{ my: 1 }}>
                                <Typography variant="body2" sx={{ color: '#94a3b8', px: 2 }}>
                                  or
                                </Typography>
                              </Divider>
                              
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => navigate('/login')}
                                sx={{ 
                                  py: 1.5,
                                  fontSize: '0.9rem',
                                  color: '#64748b',
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: 'rgba(18,82,166,0.04)',
                                    color: 'rgb(18,82,166)',
                                  }
                                }}
                              >
                                Already have an account? Sign In
                              </Button>
                          </Box>
                          </Box>
                        </Paper>




                      </Box>
                    )}

                    {/* Ethiopian Step 2: Fayda Authentication */}
                    {!isNonEthiopian && activeStep === 2 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 600,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            National ID Verification
                            </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Your details will be cross-checked and retrieved from the National ID system using your FAN (Fayda Alias Number) or Fayda alias number for identity verification.
                            </Typography>
                          </Box>

                        {/* Authentication Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 600,
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)',
                            zIndex: 1
                          }} />
                          
                          {/* Scrollable Content Container */}
                          <Box 
                            sx={{ 
                              mt: 1, 
                              textAlign: 'center',
                              p: 4
                            }}
                          >
                            <Typography variant="h6" sx={{ 
                              mb: 3, 
                              color: '#1e293b',
                              fontWeight: 600
                            }}>
                              Authentication Required
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              mb: 4, 
                              color: '#64748b',
                              lineHeight: 1.6
                            }}>
                              You will be redirected to the Fayda authentication system to verify your identity using your FAN number: <strong>{fields.fanNumber}</strong>
                            </Typography>

                            {/* Show loading state when authentication is in progress */}
                            {loading && (
                              <Box sx={{ 
                                mt: 3, 
                                p: 3, 
                                backgroundColor: '#f0f9ff', 
                                borderRadius: 2, 
                                border: '1px solid #0ea5e9',
                                textAlign: 'center'
                              }}>
                                <CircularProgress size={24} sx={{ color: 'rgb(18,82,166)', mb: 2 }} />
                                <Typography variant="body2" sx={{ color: 'rgb(18,82,166)', fontWeight: 500 }}>
                                  ✅ Authentication successful! Redirecting to profile review...
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1 }}>
                                  Please wait while we prepare your profile information.
                                </Typography>
                              </Box>
                            )}

                          <Button
                            variant="contained"
                              color="primary"
                            onClick={handleSignIn}
                              disabled={faydaLoading || loading}
                              size="small"
                              sx={{ 
                                py: 1.5,
                                px: 3,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                backgroundColor: 'rgb(18,82,166)',
                                boxShadow: '0 4px 14px 0 rgba(18,82,166,0.39)',
                                '&:hover': {
                                  backgroundColor: 'rgb(14,65,130)',
                                  boxShadow: '0 6px 20px 0 rgba(18,82,166,0.5)',
                                  transform: 'translateY(-1px)',
                                },
                                '&:disabled': {
                                  background: '#e2e8f0',
                                  color: '#94a3b8',
                                  boxShadow: 'none',
                                  transform: 'none',
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {faydaLoading ? 'Redirecting...' : loading ? 'Processing...' : 'Continue Registration with Fayda'}
                          </Button>

                          </Box>
                        </Paper>
                        </Box>
                    )}

                    {/* Ethiopian Step 3: Profile Review */}
                    {!isNonEthiopian && activeStep === 3 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 800,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <AccountCircleIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                            </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Profile Review
                              </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please review your profile information retrieved from Fayda authentication.
                              </Typography>
                        </Box>

                        {/* Profile Review Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 800,
                          p: { xs: 2, md: 4 },
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                          }} />
                          
                          <Box sx={{ mt: 1 }}>
                            <Grid container spacing={4}>
                              {/* Profile Picture - First on mobile, right side on desktop */}
                              <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
                                {profilePicture && (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    p: 2,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 2,
                                    border: '1px solid #e2e8f0'
                                  }}>
                                    <Typography variant="h6" sx={{ 
                                      mb: 2, 
                                      color: '#1e293b',
                                      fontWeight: 600
                                    }}>
                                      Profile Picture
                                    </Typography>
                                    <Box sx={{
                                      border: '3px solid rgb(18,82,166)',
                                      borderRadius: 2,
                                      p: 1,
                                      backgroundColor: '#ffffff'
                                    }}>
                                      <img
                                        src={profilePicture}
                                        alt="Profile"
                                        style={{ 
                                          maxWidth: '150px', 
                                          maxHeight: '150px', 
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                )}
                              </Grid>
                              
                              {/* Personal Information */}
                              <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
                                <Typography variant="h6" sx={{ 
                                  mb: 3, 
                                  color: '#1e293b',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                  <AccountCircleIcon sx={{ color: 'rgb(18,82,166)', fontSize: 24 }} />
                                  Personal Information
                                </Typography>
                                
                                  <List>
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccountCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Full Name"
                                      secondary={userInfo?.name || 'Not available'}
                                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                      secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                    />
                                  </ListItem>
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <PhoneIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Phone Number"
                                      secondary={userInfo?.phone_number || 'Not available'}
                                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                      secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                    />
                                  </ListItem>
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <CalendarTodayIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Date of Birth"
                                      secondary={userInfo?.birthdate || 'Not available'}
                                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                      secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                    />
                                  </ListItem>
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <GenderIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Gender"
                                      secondary={userInfo?.gender || 'Not available'}
                                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                      secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                    />
                                  </ListItem>
                                  <ListItem sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <HomeIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Address"
                                      secondary={`${address?.region || ''} ${address?.zone || ''} ${address?.woreda || ''} ${address?.kebele || ''}`.trim() || 'Not available'}
                                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: '#374151' }}
                                      secondaryTypographyProps={{ variant: 'body2', color: '#6b7280' }}
                                    />
                                  </ListItem>
                                  </List>
                              </Grid>


                            </Grid>
                                </Box>
                        </Paper>
                                  </Box>
                                )}

                    {/* Non-Ethiopian Step 1: Personal Details */}
                    {isNonEthiopian && activeStep === 1 && (
                                              <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          maxWidth: 800,
                          mx: 'auto',
                          p: { xs: 1, md: 2 }
                        }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: { xs: 3, md: 4 },
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <AccountCircleIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                            </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px',
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                          }}>
                            Personal Details
                              </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto',
                            fontSize: { xs: '0.875rem', md: '1rem' }
                          }}>
                            Please provide your basic personal information and upload your photograph.
                              </Typography>
                              </Box>

                        {/* Form Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 800,
                          backgroundColor: '#ffffff',
                          borderRadius: { xs: 2, md: 3 },
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)',
                            zIndex: 1
                          }} />
                          
                          {/* Scrollable Content Container */}
                          <Box 
                            sx={{ 
                              mt: 1,
                              p: { xs: 2, md: 4 }
                            }}
                          >
                            <Grid container spacing={{ xs: 2, md: 4 }} sx={{ alignItems: 'stretch' }}>
                                                              {/* Left Column - Personal Information */}
                                <Grid item xs={12} md={8} sx={{ px: { xs: 1, md: 0 }, order: { xs: 1, md: 1 } }}>
                                <Typography variant="h6" sx={{ 
                                  mb: { xs: 2, md: 3 }, 
                                  color: '#1e293b',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  fontSize: { xs: '1.125rem', md: '1.25rem' }
                                }}>
                                  <AccountCircleIcon sx={{ color: 'rgb(18,82,166)', fontSize: { xs: 20, md: 24 } }} />
                                  Personal Information
                                </Typography>
                                
                                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 2 }}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="firstName"
                                      name="firstName"
                                      label="First Name"
                                      placeholder="Enter your first name"
                                      value={fields.firstName}
                                      onChange={handleChange}
                                      error={!!errors.firstName}
                                      helperText={errors.firstName}
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        },
                                        // Mobile-specific fixes for text fields
                                        '& .MuiInputLabel-root': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          '&.Mui-focused': {
                                            color: 'rgb(18,82,166)',
                                          },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          padding: { xs: '12px 14px', md: '14px 14px' },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          minHeight: { xs: '48px', md: '48px' },
                                        },
                                        '& .MuiFormHelperText-root': {
                                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                                        },
                                        // Mobile-specific fixes
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderWidth: { xs: '1px', md: '1px' },
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="fatherName"
                                      name="fatherName"
                                      label="Father's Name"
                                      placeholder="Enter your father's name"
                                      value={fields.fatherName}
                                      onChange={handleChange}
                                      error={!!errors.fatherName}
                                      helperText={errors.fatherName}
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        },
                                        // Mobile-specific fixes for text fields
                                        '& .MuiInputLabel-root': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          '&.Mui-focused': {
                                            color: 'rgb(18,82,166)',
                                          },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          padding: { xs: '12px 14px', md: '14px 14px' },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          minHeight: { xs: '48px', md: '48px' },
                                        },
                                        '& .MuiFormHelperText-root': {
                                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                                        },
                                        // Mobile-specific fixes
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderWidth: { xs: '1px', md: '1px' },
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="grandFatherName"
                                      name="grandFatherName"
                                      label="Grandfather's Name"
                                      placeholder="Enter your grandfather's name"
                                      value={fields.grandFatherName}
                                      onChange={handleChange}
                                      error={!!errors.grandFatherName}
                                      helperText={errors.grandFatherName}
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        },
                                        // Mobile-specific fixes for text fields
                                        '& .MuiInputLabel-root': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          '&.Mui-focused': {
                                            color: 'rgb(18,82,166)',
                                          },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          padding: { xs: '12px 14px', md: '14px 14px' },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          minHeight: { xs: '48px', md: '48px' },
                                        },
                                        '& .MuiFormHelperText-root': {
                                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                                        },
                                        // Mobile-specific fixes
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderWidth: { xs: '1px', md: '1px' },
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required error={!!errors.gender} size="small">
                                      <InputLabel>Gender</InputLabel>
                                      <Select
                                        name="gender"
                                        value={fields.gender}
                                        onChange={handleChange}
                                        label="Gender"
                                        sx={{ 
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        }}
                                      >
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                      </Select>
                                      {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="email"
                                      name="email"
                                      label="Email Address"
                                      placeholder="Enter your email address"
                                      value={fields.email}
                                      onChange={handleChange}
                                      error={!!errors.email}
                                      helperText={errors.email}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <EmailIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20 }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        },
                                        // Mobile-specific fixes for text fields
                                        '& .MuiInputLabel-root': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          '&.Mui-focused': {
                                            color: 'rgb(18,82,166)',
                                          },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          padding: { xs: '12px 14px', md: '14px 14px' },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          minHeight: { xs: '48px', md: '48px' },
                                        },
                                        '& .MuiFormHelperText-root': {
                                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                                        },
                                        // Mobile-specific fixes
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderWidth: { xs: '1px', md: '1px' },
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="phoneNumber"
                                      name="phoneNumber"
                                      label="Phone Number"
                                      placeholder="Enter your phone number"
                                      value={fields.phoneNumber}
                                      onChange={handleChange}
                                      error={!!errors.phoneNumber}
                                      helperText={errors.phoneNumber}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <PhoneIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20 }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        },
                                        // Mobile-specific fixes for text fields
                                        '& .MuiInputLabel-root': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          '&.Mui-focused': {
                                            color: 'rgb(18,82,166)',
                                          },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                          fontSize: { xs: '0.875rem', md: '1rem' },
                                          padding: { xs: '12px 14px', md: '14px 14px' },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                          minHeight: { xs: '48px', md: '48px' },
                                        },
                                        '& .MuiFormHelperText-root': {
                                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                                        },
                                        // Mobile-specific fixes
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderWidth: { xs: '1px', md: '1px' },
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required error={!!errors.selectedProgram} size="small">
                                      <InputLabel>Applying for</InputLabel>
                                      <Select
                                        name="selectedProgram"
                                        value={fields.selectedProgram}
                                        onChange={handleChange}
                                        label="Applying for"
                                        sx={{ 
                                          borderRadius: 2,
                                          backgroundColor: '#f8fafc',
                                          '&:hover': {
                                            backgroundColor: '#f1f5f9',
                                          },
                                          '&.Mui-focused': {
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                          },
                                        }}
                                      >
                                        <MenuItem value="MSc">MSc (Master of Science)</MenuItem>
                                        <MenuItem value="PhD">PhD (Doctor of Philosophy)</MenuItem>
                                        <MenuItem value="Aviation Academy">Aviation Academy</MenuItem>
                                      </Select>
                                      {errors.selectedProgram && <FormHelperText>{errors.selectedProgram}</FormHelperText>}
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required error={!!errors.examCenter} size="small">
                                      <select
                                        name="examCenter"
                                        value={fields.examCenter}
                                        onChange={handleChange}
                                        style={{
                                          width: '100%',
                                          height: '40px',
                                          padding: '8px 12px',
                                          border: '1px solid #c4c4c4',
                                          borderRadius: '4px',
                                          backgroundColor: 'transparent',
                                          fontSize: '16px',
                                          color: 'rgba(0, 0, 0, 0.87)',
                                          outline: 'none',
                                          transition: 'all 0.2s ease-in-out',
                                          appearance: 'none',
                                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 9 6 6 6-6'/%3e%3c/svg%3e")`,
                                          backgroundPosition: 'right 8px center',
                                          backgroundRepeat: 'no-repeat',
                                          backgroundSize: '16px',
                                          paddingRight: '32px',
                                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                          cursor: 'pointer',
                                        }}
                                        onFocus={(e) => {
                                          e.target.style.borderColor = 'rgb(18,82,166)';
                                          e.target.style.borderWidth = '2px';
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.boxShadow = 'none';
                                        }}
                                        onBlur={(e) => {
                                          e.target.style.borderColor = '#c4c4c4';
                                          e.target.style.borderWidth = '1px';
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.boxShadow = 'none';
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.borderColor = '#000';
                                          e.target.style.backgroundColor = 'transparent';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.borderColor = '#c4c4c4';
                                          e.target.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        <option value="">Select Exam Center</option>
                                        {fields.selectedProgram === 'Aviation Academy' ? (
                                          examCenters
                                            .filter(center => center.name === "Other (Non-Ethiopian)")
                                            .map((center) => (
                                              <option key={center.id} value={center.id}>
                                                {center.name}
                                              </option>
                                            ))
                                        ) : (
                                          examCenters.map((center) => (
                                            <option key={center.id} value={center.id}>
                                              {center.name}
                                            </option>
                                          ))
                                        )}
                                      </select>
                                      {errors.examCenter && <FormHelperText>{errors.examCenter}</FormHelperText>}
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </Grid>

                              {/* Photo Upload Section - Integrated into Grid layout */}
                              <Grid item xs={12} md={4} sx={{ px: { xs: 1, md: 0 }, order: { xs: 3, md: 2 } }}>
                                <Typography variant="h6" sx={{ 
                                  mb: { xs: 2, md: 3 }, 
                                  color: '#1e293b',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  fontSize: { xs: '1.125rem', md: '1.25rem' }
                                }}>
                                  <PhotoCameraIcon sx={{ color: 'rgb(18,82,166)', fontSize: { xs: 20, md: 24 } }} />
                                  Photograph
                                </Typography>
                                
                                <Box sx={{ 
                                  border: '2px dashed #cbd5e1', 
                                  borderRadius: { xs: 2, md: 3 }, 
                                  p: { xs: 2, md: 3 }, 
                                  textAlign: 'center',
                                  backgroundColor: '#f8fafc',
                                  minHeight: { xs: 180, md: 200 },
                                  maxHeight: { xs: 220, md: 250 },
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  '&:hover': {
                                    borderColor: 'rgb(18,82,166)',
                                    backgroundColor: '#f1f5f9',
                                  }
                                }}>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                    style={{ display: 'none' }}
                                    id="photo-upload"
                                  />
                                  <label htmlFor="photo-upload">
                                    <Box sx={{ cursor: 'pointer' }}>
                                      {fields.photo ? (
                                        <Box sx={{ textAlign: 'center' }}>
                                          <Avatar 
                                            src={URL.createObjectURL(fields.photo)} 
                                            alt="Profile" 
                                            sx={{ 
                                              width: { xs: 100, md: 120 }, 
                                              height: { xs: 100, md: 120 },
                                              mx: 'auto',
                                              mb: 2,
                                              border: '3px solid #ffffff',
                                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                            }} 
                                          />
                                          <Typography variant="body2" sx={{ color: 'rgb(18,82,166)', fontWeight: 600, mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                            ✓ Photo uploaded
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                            Click to change
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Box>
                                          <PhotoCameraIcon sx={{ fontSize: { xs: 40, md: 48 }, color: 'rgb(18,82,166)', mb: 2 }} />
                                          <Typography variant="h6" sx={{ mb: 1, color: 'rgb(18,82,166)', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                                            Upload 4x4 Photo
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                            Click to upload your photograph
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                            Supports: JPG, PNG (Max 2MB)
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </label>
                                </Box>
                                {errors.photo && (
                                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    {errors.photo}
                                  </Typography>
                                )}
                              </Grid>

                            </Grid>
                          </Box>
                        </Paper>

                      </Box>
                    )}

                    {/* Non-Ethiopian Step 2: Contact & Passport */}
                    {isNonEthiopian && activeStep === 2 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 700,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <FlagIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Passport Information
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please provide your passport details and upload the front page.
                          </Typography>
                        </Box>

                        {/* Form Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 700,
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)',
                            zIndex: 1
                          }} />
                          
                          {/* Scrollable Content Container */}
                          <Box 
                            sx={{ 
                              mt: 1,
                              p: 4
                            }}
                          >
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.countryOfOrigin} size="small">
                                  <InputLabel>Select Country of Origin</InputLabel>
                                  <Select
                                    name="countryOfOrigin"
                                    value={fields.countryOfOrigin}
                                    onChange={handleChange}
                                    label="Select Country of Origin"
                                    sx={{ 
                                      borderRadius: 2,
                                      backgroundColor: '#f8fafc',
                                      '&:hover': {
                                        backgroundColor: '#f1f5f9',
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                      },
                                    }}
                                  >
                                    <MenuItem value="Afghanistan">Afghanistan</MenuItem>
                                    <MenuItem value="Albania">Albania</MenuItem>
                                    <MenuItem value="Algeria">Algeria</MenuItem>
                                    <MenuItem value="Andorra">Andorra</MenuItem>
                                    <MenuItem value="Angola">Angola</MenuItem>
                                    <MenuItem value="Antigua and Barbuda">Antigua and Barbuda</MenuItem>
                                    <MenuItem value="Argentina">Argentina</MenuItem>
                                    <MenuItem value="Armenia">Armenia</MenuItem>
                                    <MenuItem value="Australia">Australia</MenuItem>
                                    <MenuItem value="Austria">Austria</MenuItem>
                                    <MenuItem value="Azerbaijan">Azerbaijan</MenuItem>
                                    <MenuItem value="Bahamas">Bahamas</MenuItem>
                                    <MenuItem value="Bahrain">Bahrain</MenuItem>
                                    <MenuItem value="Bangladesh">Bangladesh</MenuItem>
                                    <MenuItem value="Barbados">Barbados</MenuItem>
                                    <MenuItem value="Belarus">Belarus</MenuItem>
                                    <MenuItem value="Belgium">Belgium</MenuItem>
                                    <MenuItem value="Belize">Belize</MenuItem>
                                    <MenuItem value="Benin">Benin</MenuItem>
                                    <MenuItem value="Bhutan">Bhutan</MenuItem>
                                    <MenuItem value="Bolivia">Bolivia</MenuItem>
                                    <MenuItem value="Bosnia and Herzegovina">Bosnia and Herzegovina</MenuItem>
                                    <MenuItem value="Botswana">Botswana</MenuItem>
                                    <MenuItem value="Brazil">Brazil</MenuItem>
                                    <MenuItem value="Brunei">Brunei</MenuItem>
                                    <MenuItem value="Bulgaria">Bulgaria</MenuItem>
                                    <MenuItem value="Burkina Faso">Burkina Faso</MenuItem>
                                    <MenuItem value="Burundi">Burundi</MenuItem>
                                    <MenuItem value="Cabo Verde">Cabo Verde</MenuItem>
                                    <MenuItem value="Cambodia">Cambodia</MenuItem>
                                    <MenuItem value="Cameroon">Cameroon</MenuItem>
                                    <MenuItem value="Canada">Canada</MenuItem>
                                    <MenuItem value="Central African Republic">Central African Republic</MenuItem>
                                    <MenuItem value="Chad">Chad</MenuItem>
                                    <MenuItem value="Chile">Chile</MenuItem>
                                    <MenuItem value="China">China</MenuItem>
                                    <MenuItem value="Colombia">Colombia</MenuItem>
                                    <MenuItem value="Comoros">Comoros</MenuItem>
                                    <MenuItem value="Congo">Congo</MenuItem>
                                    <MenuItem value="Costa Rica">Costa Rica</MenuItem>
                                    <MenuItem value="Croatia">Croatia</MenuItem>
                                    <MenuItem value="Cuba">Cuba</MenuItem>
                                    <MenuItem value="Cyprus">Cyprus</MenuItem>
                                    <MenuItem value="Czech Republic">Czech Republic</MenuItem>
                                    <MenuItem value="Democratic Republic of the Congo">Democratic Republic of the Congo</MenuItem>
                                    <MenuItem value="Denmark">Denmark</MenuItem>
                                    <MenuItem value="Djibouti">Djibouti</MenuItem>
                                    <MenuItem value="Dominica">Dominica</MenuItem>
                                    <MenuItem value="Dominican Republic">Dominican Republic</MenuItem>
                                    <MenuItem value="East Timor">East Timor</MenuItem>
                                    <MenuItem value="Ecuador">Ecuador</MenuItem>
                                    <MenuItem value="Egypt">Egypt</MenuItem>
                                    <MenuItem value="El Salvador">El Salvador</MenuItem>
                                    <MenuItem value="Equatorial Guinea">Equatorial Guinea</MenuItem>
                                    <MenuItem value="Eritrea">Eritrea</MenuItem>
                                    <MenuItem value="Estonia">Estonia</MenuItem>
                                    <MenuItem value="Eswatini">Eswatini</MenuItem>
                                    <MenuItem value="Fiji">Fiji</MenuItem>
                                    <MenuItem value="Finland">Finland</MenuItem>
                                    <MenuItem value="France">France</MenuItem>
                                    <MenuItem value="Gabon">Gabon</MenuItem>
                                    <MenuItem value="Gambia">Gambia</MenuItem>
                                    <MenuItem value="Georgia">Georgia</MenuItem>
                                    <MenuItem value="Germany">Germany</MenuItem>
                                    <MenuItem value="Ghana">Ghana</MenuItem>
                                    <MenuItem value="Greece">Greece</MenuItem>
                                    <MenuItem value="Grenada">Grenada</MenuItem>
                                    <MenuItem value="Guatemala">Guatemala</MenuItem>
                                    <MenuItem value="Guinea">Guinea</MenuItem>
                                    <MenuItem value="Guinea-Bissau">Guinea-Bissau</MenuItem>
                                    <MenuItem value="Guyana">Guyana</MenuItem>
                                    <MenuItem value="Haiti">Haiti</MenuItem>
                                    <MenuItem value="Honduras">Honduras</MenuItem>
                                    <MenuItem value="Hungary">Hungary</MenuItem>
                                    <MenuItem value="Iceland">Iceland</MenuItem>
                                    <MenuItem value="India">India</MenuItem>
                                    <MenuItem value="Indonesia">Indonesia</MenuItem>
                                    <MenuItem value="Iran">Iran</MenuItem>
                                    <MenuItem value="Iraq">Iraq</MenuItem>
                                    <MenuItem value="Ireland">Ireland</MenuItem>
                                    <MenuItem value="Israel">Israel</MenuItem>
                                    <MenuItem value="Italy">Italy</MenuItem>
                                    <MenuItem value="Ivory Coast">Ivory Coast</MenuItem>
                                    <MenuItem value="Jamaica">Jamaica</MenuItem>
                                    <MenuItem value="Japan">Japan</MenuItem>
                                    <MenuItem value="Jordan">Jordan</MenuItem>
                                    <MenuItem value="Kazakhstan">Kazakhstan</MenuItem>
                                    <MenuItem value="Kenya">Kenya</MenuItem>
                                    <MenuItem value="Kiribati">Kiribati</MenuItem>
                                    <MenuItem value="Kuwait">Kuwait</MenuItem>
                                    <MenuItem value="Kyrgyzstan">Kyrgyzstan</MenuItem>
                                    <MenuItem value="Laos">Laos</MenuItem>
                                    <MenuItem value="Latvia">Latvia</MenuItem>
                                    <MenuItem value="Lebanon">Lebanon</MenuItem>
                                    <MenuItem value="Lesotho">Lesotho</MenuItem>
                                    <MenuItem value="Liberia">Liberia</MenuItem>
                                    <MenuItem value="Libya">Libya</MenuItem>
                                    <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                                    <MenuItem value="Lithuania">Lithuania</MenuItem>
                                    <MenuItem value="Luxembourg">Luxembourg</MenuItem>
                                    <MenuItem value="Madagascar">Madagascar</MenuItem>
                                    <MenuItem value="Malawi">Malawi</MenuItem>
                                    <MenuItem value="Malaysia">Malaysia</MenuItem>
                                    <MenuItem value="Maldives">Maldives</MenuItem>
                                    <MenuItem value="Mali">Mali</MenuItem>
                                    <MenuItem value="Malta">Malta</MenuItem>
                                    <MenuItem value="Marshall Islands">Marshall Islands</MenuItem>
                                    <MenuItem value="Mauritania">Mauritania</MenuItem>
                                    <MenuItem value="Mauritius">Mauritius</MenuItem>
                                    <MenuItem value="Mexico">Mexico</MenuItem>
                                    <MenuItem value="Micronesia">Micronesia</MenuItem>
                                    <MenuItem value="Moldova">Moldova</MenuItem>
                                    <MenuItem value="Monaco">Monaco</MenuItem>
                                    <MenuItem value="Mongolia">Mongolia</MenuItem>
                                    <MenuItem value="Montenegro">Montenegro</MenuItem>
                                    <MenuItem value="Morocco">Morocco</MenuItem>
                                    <MenuItem value="Mozambique">Mozambique</MenuItem>
                                    <MenuItem value="Myanmar">Myanmar</MenuItem>
                                    <MenuItem value="Namibia">Namibia</MenuItem>
                                    <MenuItem value="Nauru">Nauru</MenuItem>
                                    <MenuItem value="Nepal">Nepal</MenuItem>
                                    <MenuItem value="Netherlands">Netherlands</MenuItem>
                                    <MenuItem value="New Zealand">New Zealand</MenuItem>
                                    <MenuItem value="Nicaragua">Nicaragua</MenuItem>
                                    <MenuItem value="Niger">Niger</MenuItem>
                                    <MenuItem value="Nigeria">Nigeria</MenuItem>
                                    <MenuItem value="North Korea">North Korea</MenuItem>
                                    <MenuItem value="North Macedonia">North Macedonia</MenuItem>
                                    <MenuItem value="Norway">Norway</MenuItem>
                                    <MenuItem value="Oman">Oman</MenuItem>
                                    <MenuItem value="Pakistan">Pakistan</MenuItem>
                                    <MenuItem value="Palau">Palau</MenuItem>
                                    <MenuItem value="Palestine">Palestine</MenuItem>
                                    <MenuItem value="Panama">Panama</MenuItem>
                                    <MenuItem value="Papua New Guinea">Papua New Guinea</MenuItem>
                                    <MenuItem value="Paraguay">Paraguay</MenuItem>
                                    <MenuItem value="Peru">Peru</MenuItem>
                                    <MenuItem value="Philippines">Philippines</MenuItem>
                                    <MenuItem value="Poland">Poland</MenuItem>
                                    <MenuItem value="Portugal">Portugal</MenuItem>
                                    <MenuItem value="Qatar">Qatar</MenuItem>
                                    <MenuItem value="Romania">Romania</MenuItem>
                                    <MenuItem value="Russia">Russia</MenuItem>
                                    <MenuItem value="Rwanda">Rwanda</MenuItem>
                                    <MenuItem value="Saint Kitts and Nevis">Saint Kitts and Nevis</MenuItem>
                                    <MenuItem value="Saint Lucia">Saint Lucia</MenuItem>
                                    <MenuItem value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</MenuItem>
                                    <MenuItem value="Samoa">Samoa</MenuItem>
                                    <MenuItem value="San Marino">San Marino</MenuItem>
                                    <MenuItem value="Sao Tome and Principe">Sao Tome and Principe</MenuItem>
                                    <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                                    <MenuItem value="Senegal">Senegal</MenuItem>
                                    <MenuItem value="Serbia">Serbia</MenuItem>
                                    <MenuItem value="Seychelles">Seychelles</MenuItem>
                                    <MenuItem value="Sierra Leone">Sierra Leone</MenuItem>
                                    <MenuItem value="Singapore">Singapore</MenuItem>
                                    <MenuItem value="Slovakia">Slovakia</MenuItem>
                                    <MenuItem value="Slovenia">Slovenia</MenuItem>
                                    <MenuItem value="Solomon Islands">Solomon Islands</MenuItem>
                                    <MenuItem value="Somalia">Somalia</MenuItem>
                                    <MenuItem value="South Africa">South Africa</MenuItem>
                                    <MenuItem value="South Korea">South Korea</MenuItem>
                                    <MenuItem value="South Sudan">South Sudan</MenuItem>
                                    <MenuItem value="Spain">Spain</MenuItem>
                                    <MenuItem value="Sri Lanka">Sri Lanka</MenuItem>
                                    <MenuItem value="Sudan">Sudan</MenuItem>
                                    <MenuItem value="Suriname">Suriname</MenuItem>
                                    <MenuItem value="Sweden">Sweden</MenuItem>
                                    <MenuItem value="Switzerland">Switzerland</MenuItem>
                                    <MenuItem value="Syria">Syria</MenuItem>
                                    <MenuItem value="Taiwan">Taiwan</MenuItem>
                                    <MenuItem value="Tajikistan">Tajikistan</MenuItem>
                                    <MenuItem value="Tanzania">Tanzania</MenuItem>
                                    <MenuItem value="Thailand">Thailand</MenuItem>
                                    <MenuItem value="Togo">Togo</MenuItem>
                                    <MenuItem value="Tonga">Tonga</MenuItem>
                                    <MenuItem value="Trinidad and Tobago">Trinidad and Tobago</MenuItem>
                                    <MenuItem value="Tunisia">Tunisia</MenuItem>
                                    <MenuItem value="Turkey">Turkey</MenuItem>
                                    <MenuItem value="Turkmenistan">Turkmenistan</MenuItem>
                                    <MenuItem value="Tuvalu">Tuvalu</MenuItem>
                                    <MenuItem value="Uganda">Uganda</MenuItem>
                                    <MenuItem value="Ukraine">Ukraine</MenuItem>
                                    <MenuItem value="United Arab Emirates">United Arab Emirates</MenuItem>
                                    <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                    <MenuItem value="United States">United States</MenuItem>
                                    <MenuItem value="Uruguay">Uruguay</MenuItem>
                                    <MenuItem value="Uzbekistan">Uzbekistan</MenuItem>
                                    <MenuItem value="Vanuatu">Vanuatu</MenuItem>
                                    <MenuItem value="Vatican City">Vatican City</MenuItem>
                                    <MenuItem value="Venezuela">Venezuela</MenuItem>
                                    <MenuItem value="Vietnam">Vietnam</MenuItem>
                                    <MenuItem value="Yemen">Yemen</MenuItem>
                                    <MenuItem value="Zambia">Zambia</MenuItem>
                                    <MenuItem value="Zimbabwe">Zimbabwe</MenuItem>
                                  </Select>
                                  {errors.countryOfOrigin && <FormHelperText>{errors.countryOfOrigin}</FormHelperText>}
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                  <TextField
                                    required
                                    fullWidth
                                    id="passportNumber"
                                    name="passportNumber"
                                    label="Passport Number"
                                    placeholder="Enter your passport number"
                                    value={fields.passportNumber}
                                    onChange={handleChange}
                                    error={!!errors.passportNumber}
                                    helperText={errors.passportNumber}
                                  sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      backgroundColor: '#f8fafc',
                                      '&:hover': {
                                        backgroundColor: '#f1f5f9',
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                      },
                                    },
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Box sx={{ 
                                  border: '2px dashed #cbd5e1', 
                                  borderRadius: 2, 
                                  p: 3, 
                                  textAlign: 'center',
                                  backgroundColor: '#f8fafc',
                                  '&:hover': {
                                    borderColor: 'rgb(18,82,166)',
                                    backgroundColor: '#f1f5f9',
                                  }
                                }}>
                                  <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    id="passport-photo-upload"
                                  />
                                  <label htmlFor="passport-photo-upload">
                                    <Box sx={{ cursor: 'pointer' }}>
                                      <BadgeIcon sx={{ fontSize: 48, color: 'rgb(18,82,166)', mb: 2 }} />
                                      <Typography variant="h6" sx={{ mb: 1, color: 'rgb(18,82,166)' }}>
                                        Upload Passport Front Page
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                                        Upload the front page of your passport showing your photo and personal details
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        Supports: JPG, PNG, PDF (Max 2MB)
                                      </Typography>
                                </Box>
                                  </label>
                                </Box>
                                {fields.passportPhoto && (
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
                                    <Typography variant="body2" sx={{ color: 'rgb(18,82,166)' }}>
                                      ✓ File uploaded: {fields.passportPhoto.name}
                                    </Typography>
                          </Box>
                        )}
                                {errors.passportPhoto && (
                                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    {errors.passportPhoto}
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                              </Box>
                        </Paper>
                      </Box>
                    )}





                                        {/* Non-Ethiopian Step 3: Accessibility Information */}
                    {isNonEthiopian && activeStep === 3 && fields.selectedProgram !== 'Aviation Academy' && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 800,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <AccessibilityIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Accessibility Information
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please provide information about any accessibility requirements to help us accommodate your needs.
                          </Typography>
                        </Box>

                        {/* Accessibility Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 800,
                          p: { xs: 2, md: 4 },
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                          }} />
                          
                          <Box sx={{ mt: 1 }}>


                                                          <Grid container spacing={4}>
                                {/* Visual Impairment */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" sx={{ 
                                    mb: 1.5, 
                                    color: '#1e293b',
                                    fontWeight: 600,
                                    fontSize: '0.875rem'
                                  }}>
                                    Visual Impairment
                                  </Typography>
                                  <FormControl fullWidth required error={!!errors.isBlind} size="small">
                                    <Select
                                      name="isBlind"
                                      value={fields.isBlind}
                                      onChange={handleChange}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            zIndex: 99999,
                                          }
                                        }
                                      }}
                                      sx={{ 
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                        zIndex: 9999,
                                      }}
                                    >
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                    {errors.isBlind && <FormHelperText error>{errors.isBlind}</FormHelperText>}
                                  </FormControl>
                                </Grid>

                                {/* Hearing Impairment */}
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" sx={{ 
                                    mb: 1.5, 
                                    color: '#1e293b',
                                    fontWeight: 600,
                                    fontSize: '0.875rem'
                                  }}>
                                    Hearing Impairment
                                  </Typography>
                                  <FormControl fullWidth required error={!!errors.isDeaf} size="small">
                                    <Select
                                      name="isDeaf"
                                      value={fields.isDeaf}
                                      onChange={handleChange}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            zIndex: 99999,
                                          }
                                        }
                                      }}
                                      sx={{ 
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                        zIndex: 9999,
                                      }}
                                    >
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                    {errors.isDeaf && <FormHelperText error>{errors.isDeaf}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                              </Grid>

                            {/* Additional Information */}
                            <Box sx={{ mt: 4, p: 3, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>
                                <strong>Note:</strong> This information helps us provide appropriate accommodations during the examination.
                              </Typography>
                            </Box>
                            {/* Bottom padding to ensure accessibility note is fully visible */}
                            <Box sx={{ height: { xs: 20, md: 40 } }} />
                          </Box>
                        </Paper>
                          </Box>
                    )}

                    {/* Non-Ethiopian Step 4: Preview & Confirm */}
                    {isNonEthiopian && activeStep === 4 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 900,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Final Review & Confirmation
                      </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please carefully review all your information before submitting your NGAT registration
                          </Typography>
                        </Box>

                        {/* Review Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 900,
                          p: { xs: 2, md: 4 },
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                          }} />
                          
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="h6" sx={{ 
                              mb: 3, 
                              color: '#1e293b',
                              fontWeight: 600
                            }}>
                              Registration Information Summary
                            </Typography>
                            
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                              {/* Photo Section - First on mobile, right side on desktop */}
                              <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center',
                                  p: 2,
                                  backgroundColor: '#f8fafc',
                                  borderRadius: 2,
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: '#1e293b',
                                    fontWeight: 600
                                  }}>
                                    Profile Picture
                                  </Typography>
                                  {fields.photo ? (
                                    <Box sx={{ position: 'relative' }}>
                                      <Avatar
                                        src={URL.createObjectURL(fields.photo)}
                                        sx={{ 
                                          width: { xs: 80, md: 120 }, 
                                          height: { xs: 80, md: 120 },
                                          border: '2px solid rgb(18,82,166)'
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ 
                                      width: { xs: 80, md: 120 }, 
                                      height: { xs: 80, md: 120 },
                                      backgroundColor: '#e2e8f0',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <AccountCircleIcon sx={{ fontSize: { xs: 40, md: 60 }, color: '#94a3b8' }} />
                                    </Box>
                                  )}
                                  
                                  {/* Passport Photo/File for Non-Ethiopian */}
                                  {!fields.isEthiopian && fields.passportPhoto && (
                                    <Box sx={{ 
                                      mt: 3, 
                                      width: '100%',
                                      pt: 2,
                                      borderTop: '1px solid #e2e8f0'
                                    }}>
                                      <Typography variant="body2" sx={{ 
                                        color: '#1e293b', 
                                        fontWeight: 600, 
                                        mb: 1,
                                        textAlign: 'center'
                                      }}>
                                        Passport Document
                                      </Typography>
                                      {fields.passportPhoto.type.startsWith('image/') ? (
                                        <Box sx={{
                                          width: '100%',
                                          maxWidth: 120,
                                          height: 80,
                                          mx: 'auto',
                                          borderRadius: 1,
                                          overflow: 'hidden',
                                          border: '1px solid #e2e8f0',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        }}>
                                          <img 
                                            src={URL.createObjectURL(fields.passportPhoto)} 
                                            alt="Passport" 
                                            style={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover'
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box sx={{
                                          width: '100%',
                                          maxWidth: 120,
                                          height: 80,
                                          mx: 'auto',
                                          borderRadius: 1,
                                          border: '1px solid #e2e8f0',
                                          backgroundColor: '#f8fafc',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          p: 1
                                        }}>
                                          <Typography variant="caption" sx={{ 
                                            color: '#64748b', 
                                            textAlign: 'center',
                                            fontSize: '0.7rem'
                                          }}>
                                            {fields.passportPhoto.name}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                              
                              {/* Registration Summary - Second on mobile, left side on desktop */}
                              <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
                          <List>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccountCircleIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="First Name" 
                                      secondary={fields.firstName}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccountCircleIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Father's Name" 
                                      secondary={fields.fatherName}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccountCircleIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Grandfather's Name" 
                                      secondary={fields.grandFatherName}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <GenderIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Gender" 
                                      secondary={fields.gender}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <FlagIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Country of Origin" 
                                      secondary={fields.countryOfOrigin}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <EmailIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Email Address" 
                                      secondary={fields.email}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <PhoneIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Phone Number" 
                                      secondary={fields.phoneNumber}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <BadgeIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                    <ListItemText 
                                      primary="Passport Number" 
                                      secondary={fields.passportNumber}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                </ListItem> 
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <SchoolIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                    <ListItemText 
                                      primary="Applying for" 
                                      secondary={fields.selectedProgram}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <LocationOnIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                    <ListItemText 
                                      primary="Exam Center" 
                                      secondary={examCenters.find(center => center.id === fields.examCenter)?.name || fields.examCenter}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                </ListItem>
                                
                                {/* Accessibility Information */}
                                <ListItem sx={{ 
                                  px: 0, 
                                  py: 1,
                                  borderBottom: '1px solid #f1f5f9'
                                }}>
                                  <ListItemIcon sx={{ minWidth: 40 }}>
                                    <AccessibilityIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary="Visual Impairment" 
                                    secondary={fields.isBlind}
                                    primaryTypographyProps={{ 
                                      variant: 'body2', 
                                      fontWeight: 600,
                                      color: '#374151'
                                    }}
                                    secondaryTypographyProps={{ 
                                      variant: 'body2',
                                      color: '#6b7280'
                                    }}
                                  />
                                </ListItem>
                                
                                <ListItem sx={{ 
                                  px: 0, 
                                  py: 1,
                                  borderBottom: '1px solid #f1f5f9'
                                }}>
                                  <ListItemIcon sx={{ minWidth: 40 }}>
                                    <HearingDisabledIcon color="primary" fontSize="small" />
                                      </ListItemIcon>
                                  <ListItemText 
                                    primary="Hearing Impairment" 
                                    secondary={fields.isDeaf}
                                    primaryTypographyProps={{ 
                                      variant: 'body2', 
                                      fontWeight: 600,
                                      color: '#374151'
                                    }}
                                    secondaryTypographyProps={{ 
                                      variant: 'body2',
                                      color: '#6b7280'
                                    }}
                                  />
                                    </ListItem>
                                </List>
                              </Grid>
                              
                            </Grid>
                          </Box>
                        </Paper>

                        {/* Consent Checkbox */}
                        <Box sx={{ mt: 3, maxWidth: 900, width: '100%' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={fields.consent || false}
                                onChange={(e) => setFields(prev => ({ ...prev, consent: e.target.checked }))}
                                sx={{
                                  color: 'rgb(18,82,166)',
                                  '&.Mui-checked': {
                                    color: 'rgb(18,82,166)',
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem' }}>
                                I declare that all information provided in this registration is true, accurate, and complete to the best of my knowledge.
                              </Typography>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              '& .MuiFormControlLabel-label': {
                                marginTop: '2px'
                              }
                            }}
                          />

                        </Box>

                        {/* Progress and Success/Error Messages */}
                        {isSubmitting && (
                          <Box sx={{ width: '100%', mb: 2, mt: 3 }}>
                            <LinearProgress />
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center', color: '#64748b' }}>
                              Submitting registration...
                            </Typography>
                          </Box>
                        )}

                        {isSuccess && (
                          <Alert severity="success" sx={{ mt: 3, borderRadius: 2, maxWidth: 900 }}>
                            Registration completed successfully! Your registration request will be reviewed and you will be responded to via the provided email.
                          </Alert>
                        )}

                        {submitError && (
                          <Alert severity="error" sx={{ mt: 3, borderRadius: 2, maxWidth: 900 }}>
                            {submitError}
                          </Alert>
                        )}


                      </Box>
                    )}

                    {!isNonEthiopian && activeStep === 4 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 700,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <SchoolIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Additional Details
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please provide your contact information and program preferences to complete your registration.
                          </Typography>
                        </Box>

                        {/* Form Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 700,
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)',
                            zIndex: 1
                          }} />
                          
                          {/* Scrollable Content Container */}
                          <Box 
                            sx={{ 
                              mt: 1,
                              p: 4
                            }}
                          >
                            {/* Contact Information Section */}
                            <Box sx={{ mb: 4 }}>
                              <Typography variant="h6" sx={{ 
                                mb: 3, 
                                color: '#1e293b',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <EmailIcon sx={{ color: 'rgb(18,82,166)', fontSize: 24 }} />
                                Contact Information
                              </Typography>
                              
                              <Grid container spacing={{ xs: 2, md: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="email"
                                      name="email"
                                      label="Email Address"
                                      placeholder="Enter your email address"
                                      value={fields.email}
                                      onChange={handleChange}
                                      error={!!errors.email}
                                      helperText={errors.email}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <EmailIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20 }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{ 
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                      required
                                      fullWidth
                                      id="phoneNumber"
                                      name="phoneNumber"
                                      label="Phone Number"
                                      placeholder="Enter your phone number"
                                      value={fields.phoneNumber}
                                      onChange={handleChange}
                                      error={!!errors.phoneNumber}
                                      helperText={errors.phoneNumber}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <PhoneIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20 }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{ 
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Program & Center Details Section */}
                            <Box>
                              <Typography variant="h6" sx={{ 
                                mb: 3, 
                                color: '#1e293b',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <SchoolIcon sx={{ color: 'rgb(18,82,166)', fontSize: 24 }} />
                                Program & Center Details
                              </Typography>
                              
                              <Grid container spacing={{ xs: 2, md: 3 }}>
                                <Grid item xs={12} md={6}>
                                  <FormControl fullWidth required error={!!errors.selectedProgram} size="small">
                                    <InputLabel>Applying for</InputLabel>
                                    <Select
                                      name="selectedProgram"
                                      value={selectedProgram}
                                      onChange={(e) => {
                                        setSelectedProgram(e.target.value);
                                        setFields(prev => ({ ...prev, selectedProgram: e.target.value }));
                                      }}
                                      label="Applying for"
                                      startAdornment={
                                        <InputAdornment position="start">
                                          <SchoolIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20, ml: 1 }} />
                                        </InputAdornment>
                                      }
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            maxWidth: { xs: 'calc(100vw - 32px)', md: 'auto' },
                                            maxHeight: { xs: 'calc(100vh - 32px)', md: 'auto' },
                                            zIndex: 99999
                                          }
                                        }
                                      }}
                                      sx={{ 
                                        borderRadius: 2,
                                        backgroundColor: '#f8fafc',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                      }}
                                    >
                                      <MenuItem value="MSc">MSc (Master of Science)</MenuItem>
                                      <MenuItem value="PhD">PhD (Doctor of Philosophy)</MenuItem>
                                    </Select>
                                    {errors.selectedProgram && <FormHelperText>{errors.selectedProgram}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <FormControl fullWidth required error={!!errors.examcenter} size="small">
                                    <select
                                      name="examcenter"
                                      value={fields.examcenter}
                                      onChange={handleChange}
                                      style={{
                                        width: '100%',
                                        height: '40px',
                                        padding: '8px 12px',
                                        border: '1px solid #c4c4c4',
                                        borderRadius: '4px',
                                        backgroundColor: 'transparent',
                                        fontSize: '16px',
                                        color: 'rgba(0, 0, 0, 0.87)',
                                        outline: 'none',
                                        transition: 'all 0.2s ease-in-out',
                                        appearance: 'none',
                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 9 6 6 6-6'/%3e%3c/svg%3e")`,
                                        backgroundPosition: 'right 8px center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '16px',
                                        paddingRight: '32px',
                                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                        cursor: 'pointer',
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = 'rgb(18,82,166)';
                                        e.target.style.borderWidth = '2px';
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#c4c4c4';
                                        e.target.style.borderWidth = '1px';
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#000';
                                        e.target.style.backgroundColor = 'transparent';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#c4c4c4';
                                        e.target.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <option value="">Select Exam Center</option>
                                      {examCenters.map((center) => (
                                        <option key={center.id} value={center.id}>
                                          {center.name}
                                        </option>
                                      ))}
                                    </select>
                                    {errors.examcenter && <FormHelperText>{errors.examcenter}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Box>
                            
                            {/* Accessibility Information Section */}
                            <Box sx={{ 
                              mb: 4, 
                              mt: 6, 
                              pt: 4, 
                              borderTop: '2px solid #e2e8f0',
                              backgroundColor: '#f8fafc',
                              borderRadius: 3,
                              p: 4
                            }}>
                              <Typography variant="h6" sx={{ 
                                mb: 4, 
                                color: '#1e293b',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: '1.1rem'
                              }}>
                                <AccessibilityIcon sx={{ color: 'rgb(18,82,166)', fontSize: 28 }} />
                                Accessibility Information
                              </Typography>
                              
                              <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" sx={{ 
                                    mb: 2.5, 
                                    color: '#1e293b',
                                    fontWeight: 600,
                                    fontSize: '0.95rem'
                                  }}>
                                    Visual Impairment
                                  </Typography>
                                  <FormControl fullWidth required error={!!errors.isBlind} size="small">
                                    <Select
                                      name="isBlind"
                                      value={fields.isBlind}
                                      onChange={handleChange}
                                      startAdornment={
                                        <InputAdornment position="start">
                                          <VisibilityIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20, ml: 1 }} />
                                        </InputAdornment>
                                      }
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            maxWidth: { xs: 'calc(100vw - 32px)', md: 'auto' },
                                            maxHeight: { xs: 'calc(100vh - 32px)', md: 'auto' },
                                            zIndex: 99999
                                          }
                                        }
                                      }}
                                      sx={{ 
                                        borderRadius: 2,
                                        backgroundColor: '#ffffff',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                        zIndex: 9999,
                                      }}
                                    >
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                    {errors.isBlind && <FormHelperText error>{errors.isBlind}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" sx={{ 
                                    mb: 2.5, 
                                    color: '#1e293b',
                                    fontWeight: 600,
                                    fontSize: '0.95rem'
                                  }}>
                                    Hearing Impairment
                                  </Typography>
                                  <FormControl fullWidth required error={!!errors.isDeaf} size="small">
                                    <Select
                                      name="isDeaf"
                                      value={fields.isDeaf}
                                      onChange={handleChange}
                                      startAdornment={
                                        <InputAdornment position="start">
                                          <HearingDisabledIcon sx={{ color: 'rgb(18,82,166)', fontSize: 20, ml: 1 }} />
                                        </InputAdornment>
                                      }
                                      MenuProps={{
                                        PaperProps: {
                                          sx: {
                                            maxWidth: { xs: 'calc(100vw - 32px)', md: 'auto' },
                                            maxHeight: { xs: 'calc(100vh - 32px)', md: 'auto' },
                                            zIndex: 99999
                                          }
                                        }
                                      }}
                                      sx={{ 
                                        borderRadius: 2,
                                        backgroundColor: '#ffffff',
                                        '&:hover': {
                                          backgroundColor: '#f1f5f9',
                                        },
                                        '&.Mui-focused': {
                                          backgroundColor: '#ffffff',
                                          boxShadow: '0 0 0 2px rgba(18,82,166,0.2)',
                                        },
                                        zIndex: 9999,
                                      }}
                                    >
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                    {errors.isDeaf && <FormHelperText error>{errors.isDeaf}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                              </Grid>
                              
                              {/* Additional Information */}
                              <Box sx={{ mt: 4, p: 4, backgroundColor: '#ffffff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', lineHeight: 1.6, fontSize: '0.9rem' }}>
                                  <strong>Note:</strong> This information helps us provide appropriate accommodations during the examination.
                                </Typography>
                              </Box>
                            
                            {/* Bottom padding to ensure accessibility note is fully visible */}
                            <Box sx={{ height: { xs: 20, md: 40 } }} />
                          </Box>
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Ethiopian Step 5: Review & Submit */}
                    {!isNonEthiopian && activeStep === 5 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        maxWidth: 800,
                        mx: 'auto',
                        p: 2
                      }}>
                        {/* Header Section */}
                        <Box sx={{ 
                          textAlign: 'center', 
                          mb: 4,
                          width: '100%'
                        }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(18,82,166,0.1)',
                            mb: 2
                          }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'rgb(18,82,166)' }} />
                          </Box>
                          <Typography variant="h5" sx={{ 
                            mb: 1, 
                            color: 'rgb(18,82,166)', 
                            fontWeight: 700,
                            letterSpacing: '-0.5px'
                          }}>
                            Review & Submit
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            color: '#64748b', 
                            lineHeight: 1.6,
                            maxWidth: 480,
                            mx: 'auto'
                          }}>
                            Please review all your information before submitting your registration.
                          </Typography>
                        </Box>

                        {/* Review Card */}
                        <Paper elevation={0} sx={{ 
                          width: '100%',
                          maxWidth: 800,
                          p: { xs: 2, md: 4 },
                          backgroundColor: '#ffffff',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          position: 'relative',
                        }}>
                          {/* Decorative accent */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                          }} />
                          
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="h6" sx={{ 
                              mb: 3, 
                              color: '#1e293b',
                              fontWeight: 600
                            }}>
                              Registration Information Summary
                            </Typography>
                            
                            <Grid container spacing={{ xs: 2, md: 3 }}>
                              <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
                                <List>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccountCircleIcon color="primary" fontSize="small" />
                                      </ListItemIcon>
                                    <ListItemText 
                                      primary="Full Name" 
                                      secondary={userInfo?.name || 'Not available'}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                    </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <BadgeIcon color="primary" fontSize="small" />
                                      </ListItemIcon>
                                    <ListItemText 
                                      primary="FAN Number" 
                                      secondary={fields.fanNumber}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                    </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <EmailIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Email Address" 
                                      secondary={fields.email}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <GenderIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                      primary="Gender" 
                                      secondary={userInfo?.gender || 'Not available'}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                    </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <PhoneIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                                    <ListItemText 
                                      primary="Phone Number" 
                                      secondary={fields.phoneNumber}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                            </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <HomeIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                      primary="Address" 
                                      secondary={`${address?.region || ''} ${address?.zone || ''} ${address?.woreda || ''} ${address?.kebele || ''}`.trim() || 'Not available'}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                            />
                          </ListItem>
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <CheckCircleOutlineIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                      primary="Applying for" 
                                      secondary={selectedProgram}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                            />
                          </ListItem>
                                  <ListItem sx={{ 
                                    px: 0, 
                                    py: 1
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <SchoolIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="Exam Center" 
                                      secondary={examCenters.find(center => center.id === fields.examcenter)?.name || ''}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                            />
                          </ListItem>

                                  {/* Accessibility Information */}
                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <AccessibilityIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                      primary="Visual Impairment" 
                                      secondary={fields.isBlind}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                            />
                          </ListItem>

                                  <ListItem sx={{ 
                                    px: { xs: 1, md: 0 }, 
                                    py: { xs: 1.5, md: 1 },
                                    borderBottom: '1px solid #f1f5f9'
                                  }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 32, md: 40 } }}>
                                      <HearingDisabledIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary="Hearing Impairment" 
                                      secondary={fields.isDeaf}
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        fontWeight: 600,
                                        color: '#374151',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                      secondaryTypographyProps={{ 
                                        variant: 'body2',
                                        color: '#6b7280',
                                        sx: { wordBreak: 'break-word' }
                                      }}
                                    />
                                  </ListItem>
                          </List>
                              </Grid>
                              <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
                            <Box sx={{
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  alignItems: 'center',
                                  p: 2,
                                  backgroundColor: '#f8fafc',
                                  borderRadius: 2,
                                  border: '1px solid #e2e8f0'
                                }}>
                                  <Typography variant="body2" sx={{ 
                                    mb: 2, 
                                    fontWeight: 600,
                                    color: '#374151'
                                  }}>
                                    Profile Picture
                                  </Typography>
                                  {!userInfo?.picture && !fields.file && (
                                    <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center' }}>
                                      No profile picture available
                                    </Typography>
                                  )}
                          {(userInfo?.picture || fields.file) && (
                            <Box sx={{
                                      border: '3px solid rgb(18,82,166)', 
                                      borderRadius: 2, 
                                      p: 1,
                                      backgroundColor: '#ffffff'
                            }}>
                              <img
                                src={userInfo?.picture || (fields.file ? URL.createObjectURL(fields.file) : '')}
                                alt="Profile Photo"
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '150px', 
                                          borderRadius: '4px'
                                        }}
                              />
                            </Box>
                          )}
                        </Box>
                              </Grid>
                            </Grid>


                      </Box>
                        </Paper>
                      </Box>
                    )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    {activeStep > 0 && (
                      <Button
                        onClick={handleBack}
                        size="small"
                      >
                        Back
                      </Button>
                    )}
                    
                    {/* Navigation buttons for different flows */}
                    {!isNonEthiopian && activeStep > 0 && activeStep < 5 && activeStep !== 2 && activeStep !== 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        size="small"
                      >
                        Next
                      </Button>
                    )}
                    
                    {/* Submit button for Ethiopian step 5 */}
                    {!isNonEthiopian && activeStep === 5 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={isSuccess ? () => navigate('/') : handleSubmit}
                        disabled={isUploading || isSubmitting}
                        size="small"
                        sx={{
                          backgroundColor: isSuccess ? 'rgb(34, 197, 94)' : 'rgb(18,82,166)',
                          '&:hover': {
                            backgroundColor: isSuccess ? 'rgb(22, 163, 74)' : 'rgb(14,65,130)',
                          },
                          '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8',
                          },
                        }}
                      >
                        {isSuccess ? 'Go to Home' : (isSubmitting ? 'Submitting...' : 'Submit Registration')}
                      </Button>
                    )}
                    
                    {/* Non-Ethiopian navigation */}
                    {isNonEthiopian && activeStep < 2 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        size="small"
                        disabled={
                          (activeStep === 1 && (!fields.firstName || !fields.fatherName || !fields.grandFatherName || !fields.gender || !fields.photo))
                        }
                      >
                        Next
                    </Button>
                    )}
                    
                    {isNonEthiopian && activeStep === 2 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        size="small"
                        disabled={
                          !fields.countryOfOrigin || !fields.passportNumber || !fields.passportPhoto
                        }
                      >
                        Next
                      </Button>
                    )}
                    
                    {/* Next button for Non-Ethiopian step 3 (Accessibility) */}
                    {isNonEthiopian && activeStep === 3 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        size="small"
                        disabled={
                          !fields.isBlind || !fields.isDeaf
                        }
                        sx={{
                          backgroundColor: 'rgb(18,82,166)',
                          '&:hover': {
                            backgroundColor: 'rgb(14,65,130)',
                          },
                        }}
                      >
                        Next
                      </Button>
                    )}
                    
                    {/* Submit button for Non-Ethiopian step 4 (Preview & Confirm) */}
                    {isNonEthiopian && activeStep === 4 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={isSuccess ? () => navigate('/') : handleSubmit}
                        disabled={isSubmitting || !fields.consent}
                        size="small"
                        sx={{
                          backgroundColor: isSuccess ? 'rgb(34, 197, 94)' : 'rgb(18,82,166)',
                          '&:hover': {
                            backgroundColor: isSuccess ? 'rgb(22, 163, 74)' : 'rgb(14,65,130)',
                          },
                          '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8',
                          },
                        }}
                      >
                        {isSuccess ? 'Go to Home' : (isSubmitting ? 'Submitting...' : 'Submit Registration')}
                      </Button>
                    )}
                    

                  </Box>
                  
                  {/* Success and Error alerts for Ethiopian step 5 */}
                  {!isNonEthiopian && activeStep === 5 && (
                    <Box sx={{ mt: 3 }}>
                      {isUploading && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                          <LinearProgress variant="determinate" value={uploadProgress} />
                          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center', color: '#64748b' }}>
                            Uploading: {uploadProgress}%
                          </Typography>
                        </Box>
                      )}

                      {isSubmitting && !isUploading && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                          <LinearProgress />
                          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center', color: '#64748b' }}>
                            Submitting registration...
                          </Typography>
                        </Box>
                      )}

                      {isSuccess && (
                        <Alert severity="success" sx={{ borderRadius: 2 }}>
                          An account activation link has been sent to your email. Please follow the instructions there. It usually takes up to 5-10 minutes.
                        </Alert>
                      )}

                      {submitError && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                          {submitError}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box> 
            )}
          </Container>
        </Box>

        <Footer />
          </div>
        </ThemeProvider>
  );
}
